import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { parsePedidoInternalMessages } from '../utils/pedidoInternalMessages';
import { dispatchPedidoDerivadoAlMedico } from '../utils/pedidosRecetaMedicoSync';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import {
  getPedidoIngresoOrigenMeta,
  parsePedidoIngresoOrigen,
  notasTextWithoutInternalDuplicate,
  resolvePedidoNotasDisplay
} from '../utils/pedidoIngresoOrigen';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import AltaRapidaPedidoModal from '../components/AltaRapidaPedidoModal';
import PacienteExternoBadge from '../components/PacienteExternoBadge';
import { useAuth } from '../context/AuthContext';
import { canCreatePedidoAltaRapida } from '../utils/userPermissions';

const ENDPOINTS = {
  pendientes: '/api/asociacion-recetas/pedidos-derivacion',
  historial: '/api/asociacion-recetas/pedidos',
  medicos: '/api/asociacion-recetas/medicos',
  derivar: (id) => `/api/asociacion-recetas/pedidos/${id}/derivar`,
  actualizarDerivacion: (id) => `/api/asociacion-recetas/pedidos/${id}/derivacion`,
  mensajeInterno: (id) => `/api/asociacion-recetas/pedidos/${id}/mensaje-interno`,
  medicamento: (id) => `/api/asociacion-recetas/medicamento/${id}`
};

const POLL_INTERVAL_MS = 8000;
const TOAST_LIFETIME_MS = 4200;

function parseInternalMessageTimestamp(timestamp) {
  const match = String(timestamp || '').match(/(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})/);
  if (!match) return 0;
  return new Date(
    Number(match[3]),
    Number(match[2]) - 1,
    Number(match[1]),
    Number(match[4]),
    Number(match[5])
  ).getTime();
}

function getPedidoInternalChatSummary(pedido) {
  const messages = parsePedidoInternalMessages(pedido?.message_history);
  const last = messages[messages.length - 1] || null;
  return {
    messages,
    last,
    count: messages.length,
    activityAt: last?.timestamp ? parseInternalMessageTimestamp(last.timestamp) : 0
  };
}

function mergePedidosForInternalChat(pedidos, historialPedidos) {
  const map = new Map();
  [...pedidos, ...historialPedidos].forEach((pedido) => {
    if (!pedido?.id) return;
    const id = Number(pedido.id);
    map.set(id, { ...(map.get(id) || {}), ...pedido });
  });
  return Array.from(map.values());
}

function normalizePatientGroupKey(pedido) {
  const dni = String(pedido?.dni || '').replace(/\D/g, '').replace(/^0+/, '');
  if (dni) return `dni:${dni}`;
  const name = String(pedido?.paciente || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (name) return `name:${name}`;
  return `id:${pedido?.id}`;
}

function pickPrimaryPedidoForInternalChat(items) {
  if (!items?.length) return null;
  return [...items].sort((a, b) => {
    if (b.activityAt !== a.activityAt) return b.activityAt - a.activityAt;
    if (b.count !== a.count) return b.count - a.count;
    return Number(b.pedido.id) - Number(a.pedido.id);
  })[0];
}

function buildInternalChatPatientGroups(candidates) {
  const map = new Map();

  candidates.forEach((item) => {
    const key = normalizePatientGroupKey(item.pedido);
    const prev = map.get(key);
    if (!prev) {
      map.set(key, {
        key,
        paciente: item.pedido.paciente || 'Paciente',
        dni: item.pedido.dni,
        items: [item]
      });
      return;
    }
    prev.items.push(item);
  });

  return Array.from(map.values())
    .map((group) => {
      const primary = pickPrimaryPedidoForInternalChat(group.items);
      const totalMessageCount = group.items.reduce((sum, row) => sum + Number(row.count || 0), 0);
      return {
        ...group,
        pedidosCount: group.items.length,
        totalMessageCount,
        activityAt: primary?.activityAt || 0,
        last: primary?.last || null,
        primaryPedidoId: primary?.pedido?.id ?? group.items[0]?.pedido?.id
      };
    })
    .sort((a, b) => {
      if (b.activityAt !== a.activityAt) return b.activityAt - a.activityAt;
      if (b.totalMessageCount !== a.totalMessageCount) return b.totalMessageCount - a.totalMessageCount;
      return String(a.paciente).localeCompare(String(b.paciente), 'es', { sensitivity: 'base' });
    });
}

function formatInternalChatGroupSubtitle(group) {
  const parts = [`DNI ${formatDniDisplay(group.dni)}`];
  if (group.pedidosCount > 1) {
    parts.push(`${group.pedidosCount} derivaciones`);
  }
  if (group.last?.text) {
    parts.push(group.last.text);
  } else if (!group.totalMessageCount) {
    parts.push('Sin mensajes aún');
  }
  return parts.join(' · ');
}

function formatDniDisplay(dni) {
  const digits = String(dni || '').replace(/\D/g, '').replace(/^0+/, '');
  if (!digits) return '—';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function formatSolicitud(dateString) {
  if (!dateString) return '';
  try {
    const s = String(dateString).trim();
    const onlyDate = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (onlyDate) {
      return `${Number(onlyDate[3])}/${Number(onlyDate[2])}/${Number(onlyDate[1])}`;
    }
    let d;
    if (/[Zz]$/.test(s) || /[+\-]\d{2}:?\d{2}$/.test(s)) {
      d = new Date(s);
    } else {
      const m = s.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
      if (!m) return '';
      d = new Date(
        Number(m[1]), Number(m[2]) - 1, Number(m[3]),
        Number(m[4]), Number(m[5]), Number(m[6])
      );
    }
    if (Number.isNaN(d.getTime())) return '';
    d.setHours(d.getHours() - 3);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())} hs`;
  } catch (e) {
    return '';
  }
}

function normalizeMedicosFromApi(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.medicos) ? payload.medicos : []);
  const cleaned = raw.map((item, idx) => {
    if (typeof item === 'string') {
      const nombre = item.trim();
      return nombre ? { id: `prestador-${idx}-${nombre}`, nombre, especialidad: null, matricula: null } : null;
    }
    if (!item || typeof item !== 'object') return null;
    const nombre = String(item.nombre || item.prestador_geclisa || '').trim();
    if (!nombre) return null;
    const id = item.id != null ? String(item.id) : `prestador-${idx}-${nombre}`;
    return {
      id,
      nombre,
      especialidad: item.especialidad || null,
      matricula: item.matricula || null
    };
  }).filter(Boolean);

  const uniqueByName = new Map();
  cleaned.forEach((m) => {
    const key = String(m.nombre || '').toLowerCase();
    if (!key) return;
    if (!uniqueByName.has(key)) uniqueByName.set(key, m);
  });
  return Array.from(uniqueByName.values()).sort((a, b) =>
    String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' })
  );
}

function formatDoctorDisplayName(nombre) {
  const raw = String(nombre || '').trim();
  if (!raw) return '—';
  if (/^(dr|dra)\.?\s/i.test(raw)) return raw;
  const isFemale = /\b(maria|ana|paula|rocio|barbara|agustina|laura|sofia|gabriela|florencia|carla|soledad)\b/i.test(raw);
  return `${isFemale ? 'Dra.' : 'Dr.'} ${raw}`;
}

/** Mismo total ⇒ mismo matiz; 0 = gris neutro. */
function tratamientosBadgeColors(n) {
  const num = Math.max(0, Math.floor(Number(n)));
  if (num <= 0) {
    return { bg: '#f1f5f9', fg: '#64748b', border: '#e2e8f0' };
  }
  const hue = (num * 47) % 360;
  return {
    bg: `hsl(${hue} 76% 91%)`,
    fg: `hsl(${hue} 48% 28%)`,
    border: `hsl(${hue} 42% 78%)`
  };
}

function PedidoIngresoOrigenBadge({ origen, conversationId = null, compact = false }) {
  const meta = getPedidoIngresoOrigenMeta(origen);
  if (!meta) return null;
  const convLabel = conversationId ? `Conv. #${conversationId}` : null;
  return (
    <span
      title={meta.title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 5 : 8,
        padding: compact ? '4px 9px' : '6px 12px',
        borderRadius: 999,
        border: '1px solid rgba(14, 165, 233, 0.38)',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 55%, #ecfeff 100%)',
        color: '#0369a1',
        fontSize: compact ? 10 : 11,
        fontWeight: 800,
        letterSpacing: compact ? '0.04em' : '0.03em',
        lineHeight: 1.2,
        boxShadow: '0 2px 10px rgba(14, 165, 233, 0.16)',
        whiteSpace: 'nowrap',
        flexShrink: 0
      }}
    >
      <span
        aria-hidden
        style={{
          width: compact ? 6 : 7,
          height: compact ? 6 : 7,
          borderRadius: '50%',
          background: 'linear-gradient(180deg, #38bdf8, #0284c7)',
          boxShadow: '0 0 0 2px rgba(56, 189, 248, 0.28)'
        }}
      />
      <span style={{ textTransform: compact ? 'none' : 'none' }}>
        {compact ? meta.short : meta.tag}
      </span>
      {!compact && meta.chip ? (
        <span
          style={{
            padding: '2px 7px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.72)',
            border: '1px solid rgba(14, 165, 233, 0.22)',
            color: '#0c4a6e',
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }}
        >
          {meta.chip}
        </span>
      ) : null}
      {convLabel ? (
        <span
          style={{
            padding: '2px 7px',
            borderRadius: 999,
            background: '#fff',
            border: '1px solid rgba(148, 163, 184, 0.35)',
            color: '#475569',
            fontSize: 9,
            fontWeight: 700
          }}
        >
          {convLabel}
        </span>
      ) : null}
    </span>
  );
}

function PedidoDerivacionOrigenNotas({ pedido }) {
  const meta = getPedidoIngresoOrigenMeta(pedido.ingreso_origen);
  const internalMessages = parsePedidoInternalMessages(pedido.message_history);
  const notasText = notasTextWithoutInternalDuplicate(
    String(pedido.notas || '').trim(),
    internalMessages
  );
  const hasOrigen = !!meta;
  const hasContent = hasOrigen || notasText || internalMessages.length > 0;
  if (!hasContent) return null;

  return (
    <div>
      <div style={styles.fieldLabel}>Origen del pedido</div>
      <div
        style={{
          border: '1px solid rgba(148,163,184,.2)',
          borderRadius: 12,
          background: 'linear-gradient(180deg,#ffffff,#f8fafc)',
          padding: '10px 12px',
          display: 'grid',
          gap: 10
        }}
      >
        {hasOrigen ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <PedidoIngresoOrigenBadge
              origen={pedido.ingreso_origen}
              conversationId={pedido.ingreso_conversation_id}
            />
          </div>
        ) : null}
        {notasText ? (
          <div style={{ ...styles.fieldValue, fontSize: 13, lineHeight: 1.5, color: '#334155', whiteSpace: 'pre-wrap' }}>
            {notasText}
          </div>
        ) : null}
        {internalMessages.length > 0 ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '.07em'
              }}
            >
              Registro de atención
            </div>
            {internalMessages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  borderRadius: 10,
                  border: '1px solid rgba(148,163,184,.18)',
                  background: '#fff',
                  padding: '8px 10px'
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>
                  {msg.author}
                  {msg.timestamp ? ` · ${msg.timestamp}` : ''}
                </div>
                <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.45, color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                  {msg.text}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TratamientosCountBadge({ count, size = 26 }) {
  const num = Math.max(0, Math.floor(Number(count)));
  const c = tratamientosBadgeColors(num);
  const label = num > 999 ? '999+' : String(num);
  const fs = num > 999 ? 8 : (num > 99 ? 9 : 11);
  const w = size;
  return (
    <span
      title={`${num} tratamiento${num === 1 ? '' : 's'} en historial`}
      style={{
        flexShrink: 0,
        minWidth: w,
        width: w,
        height: w,
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: fs,
        fontWeight: 800,
        lineHeight: 1,
        boxSizing: 'border-box'
      }}
    >
      {label}
    </span>
  );
}

function patientInitials(name) {
  return String(name || '')
    .replace(/,/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'P';
}

const DERIVACION_ESTADO_META = {
  pendiente_derivacion: { label: 'Pendiente derivación', variant: 'derivacion' },
  pendiente_aprobacion_medica: { label: 'Pendiente médico', variant: 'medico' },
  pendiente_call_center: { label: 'Pendiente Call Center', variant: 'callcenter' },
  aprobado: { label: 'Aprobado', variant: 'aprobado' },
  rechazado: { label: 'Rechazado', variant: 'rechazado' },
  en_tratamiento: { label: 'En tratamiento', variant: 'tratamiento' },
  pedido_generado_farmacia: { label: 'Pedido ingresado', variant: 'farm-ingresado' },
  en_preparacion: { label: 'En preparación', variant: 'farm-preparacion' },
  en_camino: { label: 'Entregado al cadete', variant: 'farm-camino' },
  entregado: { label: 'Entregado', variant: 'farm-entregado' },
  cancelado: { label: 'Cancelado', variant: 'farm-cancelado' }
};

const HISTORIAL_ESTADO_FILTER_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'pendiente_aprobacion_medica', label: 'Pendiente médico' },
  { value: 'aprobado', label: 'Aprobado' },
  { value: 'rechazado', label: 'Rechazado' },
  { value: 'pedido_generado_farmacia', label: 'Pedido ingresado (Farmacia)' },
  { value: 'en_preparacion', label: 'En preparación' },
  { value: 'en_camino', label: 'Entregado al cadete' },
  { value: 'entregado', label: 'Entregado' },
  { value: 'cancelado', label: 'Cancelado' },
  { value: 'en_tratamiento', label: 'En tratamiento' },
  { value: 'pendiente_call_center', label: 'Pendiente Call Center' }
];

function getDerivacionEstadoMeta(estado) {
  const key = String(estado || '').trim().toLowerCase();
  if (DERIVACION_ESTADO_META[key]) return DERIVACION_ESTADO_META[key];
  if (!key) return { label: 'Sin estado', variant: 'muted' };
  const humanized = key
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
  return { label: humanized, variant: 'muted' };
}

function DerivacionEstadoPill({ estado, className = '' }) {
  const meta = getDerivacionEstadoMeta(estado);
  return (
    <span className={`crm-gd-estado-pill crm-gd-estado-pill--${meta.variant}${className ? ` ${className}` : ''}`}>
      {meta.label}
    </span>
  );
}

function capitalizeFirstLetter(value) {
  const s = String(value || '').trim();
  if (!s || ['null', 'undefined'].includes(s.toLowerCase())) return '';
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text && !['null', 'undefined'].includes(text.toLowerCase())) return text;
  }
  return '';
}

function normalizeMedicationFields(source) {
  const cronicaRaw = String(source?.cronica ?? '').trim().toLowerCase();
  const esCronico = source?.es_cronico === true
    || source?.es_cronico === 1
    || ['1', 's', 'si', 'sí', 'true', 'yes'].includes(String(source?.es_cronico ?? '').trim().toLowerCase())
    || ['1', 's', 'si', 'sí', 'true', 'yes', 'cronico', 'crónico'].includes(cronicaRaw);

  return {
    medicamento_nombre: firstNonEmpty(
      source?.producto,
      source?.nombre_comercial,
      source?.medicamento_nombre,
      source?.nombre_generico,
      source?.medicamento
    ),
    nombre_generico: firstNonEmpty(source?.nombre_generico, source?.medicamento_generico, source?.droganombre),
    nombre_comercial: firstNonEmpty(source?.producto, source?.nombre_comercial),
    laboratorio: firstNonEmpty(source?.laboratorio, source?.medicamento_laboratorio, source?.desclaboratorio, source?.nombrelab),
    presentacion: firstNonEmpty(source?.presentacion, source?.medicamento_presentacion),
    concentracion: firstNonEmpty(source?.concentracion, source?.medicamento_concentracion),
    troquel: firstNonEmpty(source?.troquel),
    accion_nombre: firstNonEmpty(source?.accion_nombre, source?.accionnombre),
    categoria_nombre: firstNonEmpty(source?.categoria_nombre, source?.categorianombre, source?.categoria, source?.sub_categoria, source?.subrubronombre),
    condicion_venta: firstNonEmpty(source?.condicion_venta, source?.tipoventadesc),
    refrigeracion: firstNonEmpty(source?.refrigeracion),
    es_cronico: esCronico,
    cronica: firstNonEmpty(source?.cronica, esCronico ? 'si' : '')
  };
}

function mergeMedicationFields(base, extra) {
  const left = normalizeMedicationFields(base || {});
  const right = normalizeMedicationFields(extra || {});
  return {
    ...left,
    medicamento_nombre: firstNonEmpty(right.medicamento_nombre, left.medicamento_nombre),
    nombre_generico: firstNonEmpty(right.nombre_generico, left.nombre_generico),
    nombre_comercial: firstNonEmpty(right.nombre_comercial, left.nombre_comercial),
    laboratorio: firstNonEmpty(right.laboratorio, left.laboratorio),
    presentacion: firstNonEmpty(right.presentacion, left.presentacion),
    concentracion: firstNonEmpty(right.concentracion, left.concentracion),
    troquel: firstNonEmpty(right.troquel, left.troquel),
    accion_nombre: firstNonEmpty(right.accion_nombre, left.accion_nombre),
    categoria_nombre: firstNonEmpty(right.categoria_nombre, left.categoria_nombre),
    condicion_venta: firstNonEmpty(right.condicion_venta, left.condicion_venta),
    refrigeracion: firstNonEmpty(right.refrigeracion, left.refrigeracion),
    es_cronico: Boolean(right.es_cronico || left.es_cronico),
    cronica: firstNonEmpty(right.cronica, left.cronica)
  };
}

function needsMedicationEnrichment(pedido) {
  if (!Number.isFinite(Number(pedido?.medicamento_id)) || Number(pedido?.medicamento_id) <= 0) return false;
  if (!String(pedido?.medicamento_nombre || '').trim()) return true;
  return !String(pedido?.laboratorio || '').trim()
    || !String(pedido?.troquel || '').trim()
    || !String(pedido?.accion_nombre || '').trim();
}

function buildMedicationDisplay(pedido) {
  const details = normalizeMedicationFields(pedido);
  const isCronicoCategory = ['cronico', 'crónico'].includes(String(details.categoria_nombre || '').trim().toLowerCase());
  const comercial = firstNonEmpty(
    pedido?.nombre_comercial,
    pedido?.producto,
    details.nombre_comercial,
    details.medicamento_nombre,
    pedido?.medicamento
  );
  const generico = capitalizeFirstLetter(details.nombre_generico);
  const presentacion = details.presentacion;
  const concentracionRaw = details.concentracion;
  const laboratorioRaw = details.laboratorio;
  const laboratorio = laboratorioRaw && laboratorioRaw !== '0' ? laboratorioRaw : '';
  const fallback = firstNonEmpty(details.medicamento_nombre, pedido?.medicamento);
  const title = comercial || fallback || generico || '—';
  const titleKey = title.toLowerCase();
  const concentrationKey = concentracionRaw.toLowerCase();
  const titleContainsConcentration = concentrationKey && title.toLowerCase().includes(concentrationKey);
  const presentationContainsConcentration = concentrationKey && presentacion.toLowerCase().includes(concentrationKey);
  const concentracion = /[a-z]/i.test(concentracionRaw) && !titleContainsConcentration && !presentationContainsConcentration
    ? concentracionRaw
    : '';
  const genericLine = generico && generico.toLowerCase() !== titleKey
    ? `Droga: ${generico}`
    : '';
  const accionNombre = capitalizeFirstLetter(details.accion_nombre);
  const categoriaNombre = isCronicoCategory ? '' : capitalizeFirstLetter(details.categoria_nombre);
  const requerimientos = [
    details.es_cronico ? 'Crónico' : '',
    String(details.refrigeracion || '').trim().toUpperCase() === 'S' ? 'Refrigerado' : ''
  ].filter(Boolean);
  const laboratoryLabel = laboratorio ? `Lab. ${laboratorio}` : '';
  const meta = [
    concentracion,
    presentacion,
    accionNombre,
    categoriaNombre,
    ...requerimientos
  ].filter(Boolean);

  const detailLines = [
    details.condicion_venta ? `Condición de venta: ${details.condicion_venta}. Requiere indicación médica.` : ''
  ].filter(Boolean);

  return { title, genericLine, laboratoryLabel, meta, detailLines };
}

function normalizeMedicoComparable(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/^(dr\.?|dra\.?)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function renderEyeIcon(isOpen) {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true
  };
  if (isOpen) {
    return (
      <svg {...common}>
        <path d="M4 4 20 20" />
        <path d="M10.7 6.2A10.8 10.8 0 0 1 12 6c6.5 0 10 6 10 6a18 18 0 0 1-3.3 4.2" />
        <path d="M6.2 8.5A18.5 18.5 0 0 0 2 12s3.5 6 10 6c.7 0 1.4-.1 2-.2" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
      <circle cx="12" cy="12" r="2.8" />
    </svg>
  );
}

function pedidoAsignadoAMedico(pedido, medico) {
  if (!medico || !pedido) return false;
  const pid = pedido.medico_id;
  const mid = medico.id;
  if (pid != null && String(pid).trim() !== '' && String(pid) === String(mid)) return true;
  if (
    Number.isFinite(Number(pid))
    && Number.isFinite(Number(mid))
    && Number(pid) === Number(mid)
  ) return true;
  const pn = normalizeMedicoComparable(pedido.medico || pedido.prestador_geclisa);
  const mn = normalizeMedicoComparable(medico.nombre);
  return Boolean(pn && mn && pn === mn);
}

const styles = {
  page: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    height: '100%',
    overflow: 'auto'
  },
  header: {
    background: '#FFFFFF',
    border: '1px solid #DFE3EB',
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12
  },
  kpiNum: {
    fontSize: 24,
    fontWeight: 800,
    color: '#253342',
    lineHeight: 1.1
  },
  kpiLabel: {
    fontSize: 11,
    color: '#7C98B6',
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    marginTop: 4
  },
  toolbar: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  input: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #DFE3EB',
    fontSize: 13,
    background: '#fff',
    minWidth: 240,
    flex: '1 1 240px',
    maxWidth: 420
  },
  card: {
    border: '1px solid #DFE3EB',
    borderRadius: 12,
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,.05)'
  },
  cardHeader: {
    padding: '12px 14px',
    background: '#fff',
    boxShadow: 'inset 3px 0 0 #2365AB',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap'
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    background: 'linear-gradient(145deg,#eff6ff,#dbeafe)',
    color: '#1d4ed8',
    border: '1px solid rgba(37,99,235,.28)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 800,
    flexShrink: 0
  },
  pacienteName: {
    fontSize: 15,
    fontWeight: 800,
    color: '#253342',
    overflowWrap: 'anywhere'
  },
  metaRow: {
    fontSize: 12,
    color: '#7C98B6',
    overflowWrap: 'anywhere'
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    background: '#fff',
    color: '#1d4ed8',
    border: '1px solid rgba(37,99,235,.35)',
    whiteSpace: 'nowrap'
  },
  cardBody: {
    padding: '12px 14px',
    borderTop: '1px solid rgba(148,163,184,.18)',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
    gap: 16
  },
  fieldLabel: {
    fontSize: 11,
    color: '#7C98B6',
    textTransform: 'uppercase',
    letterSpacing: '.06em',
    fontWeight: 700,
    marginBottom: 4
  },
  fieldValue: {
    fontSize: 13,
    color: '#253342',
    fontWeight: 600,
    overflowWrap: 'anywhere'
  },
  derivBox: {
    border: '1px dashed #c7d2fe',
    borderRadius: 10,
    padding: 12,
    background: '#f8fbff',
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  select: {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    fontSize: 13,
    background: '#fff'
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    fontSize: 13,
    background: '#fff',
    minHeight: 56,
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  primaryBtn: {
    padding: '9px 14px',
    borderRadius: 999,
    border: '1px solid rgba(37,99,235,.35)',
    background: 'linear-gradient(180deg,#2563eb,#1d4ed8)',
    color: '#fff',
    fontWeight: 800,
    fontSize: 13,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(37,99,235,.25)'
  },
  primaryBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    boxShadow: 'none'
  },
  emptyBox: {
    border: '1px dashed #DFE3EB',
    borderRadius: 12,
    padding: 24,
    textAlign: 'center',
    color: '#7C98B6',
    background: '#fff'
  },
  helpText: {
    fontSize: 11,
    color: '#7C98B6'
  },
  loadingBox: {
    border: '1px solid #DFE3EB',
    borderRadius: 12,
    padding: 28,
    textAlign: 'center',
    color: '#7C98B6',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    minHeight: 180,
    boxShadow: '0 1px 2px rgba(0,0,0,.05)'
  },
  spinner: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    border: '3px solid #DFE3EB',
    borderTopColor: '#2365AB',
    animation: 'gestion-derivacion-spin .8s linear infinite'
  },
  secondaryBtn: {
    padding: '8px 14px',
    borderRadius: 999,
    border: '1px solid rgba(37,99,235,.35)',
    background: 'linear-gradient(180deg,#ffffff,#f8fbff)',
    color: '#1d4ed8',
    fontWeight: 800,
    cursor: 'pointer',
    fontSize: 13
  }
};

export default function GestionDerivacion() {
  const { user, permissionsLoading, refreshPermissions } = useAuth();
  const canAltaRapida = useMemo(
    () => canCreatePedidoAltaRapida(user, { permissionsLoading }),
    [user, permissionsLoading]
  );

  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);
  const [pedidos, setPedidos] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [medicosLoading, setMedicosLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [seleccion, setSeleccion] = useState({});
  const [derivMedicoBuscar, setDerivMedicoBuscar] = useState({});
  const [derivMedicoDropdownPedidoId, setDerivMedicoDropdownPedidoId] = useState(null);
  const [notas, setNotas] = useState({});
  const [toasts, setToasts] = useState([]);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [historialPedidos, setHistorialPedidos] = useState([]);
  const [historialSearch, setHistorialSearch] = useState('');
  const [historialPacienteSearch, setHistorialPacienteSearch] = useState('');
  const [historialDni, setHistorialDni] = useState('');
  const [historialEstado, setHistorialEstado] = useState('todos');
  const [historialDesde, setHistorialDesde] = useState('');
  const [historialHasta, setHistorialHasta] = useState('');
  const [historialOrder, setHistorialOrder] = useState('fecha_desc');
  const [historialPage, setHistorialPage] = useState(1);
  const [historialItemsPerPage, setHistorialItemsPerPage] = useState(6);
  const [historialDrafts, setHistorialDrafts] = useState({});
  const [historialMedicoBuscar, setHistorialMedicoBuscar] = useState({});
  const [historialMedicoDropdownPedidoId, setHistorialMedicoDropdownPedidoId] = useState(null);
  const [historialSaving, setHistorialSaving] = useState({});
  const [historialExpandedId, setHistorialExpandedId] = useState(null);
  const [internalMessageDrafts, setInternalMessageDrafts] = useState({});
  const [internalMessageSaving, setInternalMessageSaving] = useState({});
  const [internalChatPedidoId, setInternalChatPedidoId] = useState(null);
  const [internalChatView, setInternalChatView] = useState('chat');
  const [internalChatPanelOpen, setInternalChatPanelOpen] = useState(false);
  const [internalChatPickerSearch, setInternalChatPickerSearch] = useState('');
  const [medicoVistaOpen, setMedicoVistaOpen] = useState(false);
  const [restoreStatsOnMedicoVistaClose, setRestoreStatsOnMedicoVistaClose] = useState(false);
  const [medicoVistaMedicoId, setMedicoVistaMedicoId] = useState('');
  const [medicoVistaMedicoFiltro, setMedicoVistaMedicoFiltro] = useState('');
  const [medicoVistaMedicoOrder, setMedicoVistaMedicoOrder] = useState('badge_desc');
  const [medicoVistaBuscar, setMedicoVistaBuscar] = useState('');
  const [medicoVistaListaOrder, setMedicoVistaListaOrder] = useState('fecha_desc');
  const [medicosStatsOpen, setMedicosStatsOpen] = useState(false);
  const [medicosStatsSearch, setMedicosStatsSearch] = useState('');
  const [medicosStatsPage, setMedicosStatsPage] = useState(1);
  const [medicationDrawerPedido, setMedicationDrawerPedido] = useState(null);
  const historialVisibleBeforeMedicoRef = useRef(false);
  const medicoVistaMedicoPickerRef = useRef(null);
  const [medicoVistaMedicoListOpen, setMedicoVistaMedicoListOpen] = useState(false);
  const [expandedPedidoId, setExpandedPedidoId] = useState(null);
  const [modalAltaRapidaOpen, setModalAltaRapidaOpen] = useState(false);
  const derivCardBodyRefs = useRef({});
  const pedidoCardRefs = useRef({});
  const medicamentoCacheRef = useRef(new Map());
  const knownPedidoIdsRef = useRef(new Set());
  const pedidosLoadedRef = useRef(false);
  const toastTimersRef = useRef(new Map());
  const pendingToastScrollPedidoIdRef = useRef(null);
  const toastGestureRef = useRef({ id: null, startX: 0, pointerId: null, dragging: false });
  const [toastSwipeOffsets, setToastSwipeOffsets] = useState({});

  const internalChatCandidates = useMemo(() => {
    const merged = mergePedidosForInternalChat(pedidos, historialPedidos);
    return merged
      .map((pedido) => ({ pedido, ...getPedidoInternalChatSummary(pedido) }))
      .sort((a, b) => {
        if (b.activityAt !== a.activityAt) return b.activityAt - a.activityAt;
        if (b.count !== a.count) return b.count - a.count;
        return String(a.pedido.paciente || '').localeCompare(String(b.pedido.paciente || ''), 'es', { sensitivity: 'base' });
      });
  }, [pedidos, historialPedidos]);

  const lastInternalChatPedido = useMemo(() => {
    const withMessages = internalChatCandidates.filter((item) => item.count > 0);
    if (withMessages.length) return withMessages[0].pedido;
    const withMedico = internalChatCandidates.find((item) => item.pedido.medico_id || item.pedido.medico);
    return withMedico?.pedido || internalChatCandidates[0]?.pedido || null;
  }, [internalChatCandidates]);

  const lastInternalChatSummary = useMemo(
    () => (lastInternalChatPedido ? getPedidoInternalChatSummary(lastInternalChatPedido) : null),
    [lastInternalChatPedido]
  );

  const internalChatPedido = useMemo(() => {
    const id = Number(internalChatPedidoId);
    if (!id) return null;
    return internalChatCandidates.find((item) => Number(item.pedido.id) === id)?.pedido || null;
  }, [internalChatCandidates, internalChatPedidoId]);

  const internalChatPatientGroups = useMemo(
    () => buildInternalChatPatientGroups(internalChatCandidates),
    [internalChatCandidates]
  );

  const filteredInternalChatPatientGroups = useMemo(() => {
    const q = String(internalChatPickerSearch || '').trim().toLowerCase();
    if (!q) return internalChatPatientGroups;
    return internalChatPatientGroups.filter((group) => {
      const haystack = [
        group.paciente,
        group.dni,
        formatDniDisplay(group.dni),
        ...group.items.flatMap(({ pedido }) => [
          pedido.medico,
          pedido.medicamento_nombre,
          pedido.nombre_generico,
          pedido.nombre_comercial
        ])
      ].map((value) => String(value || '').toLowerCase()).join(' | ');
      return haystack.includes(q);
    });
  }, [internalChatPatientGroups, internalChatPickerSearch]);

  function openInternalChat(pedidoId, { openPanel = true, view = 'chat' } = {}) {
    setInternalChatPedidoId(pedidoId);
    setInternalChatPickerSearch('');
    setInternalChatView(view);
    if (openPanel) setInternalChatPanelOpen(true);
  }

  function openInternalChatLauncher() {
    setInternalChatPickerSearch('');
    setInternalChatView('chat');
    if (lastInternalChatPedido) {
      setInternalChatPedidoId(lastInternalChatPedido.id);
    } else {
      setInternalChatPedidoId(null);
    }
    setInternalChatPanelOpen(true);
  }

  function goToInternalChatPickerView() {
    setInternalChatView('picker');
  }

  function goToInternalChatConversationView() {
    if (internalChatPedidoId != null) {
      setInternalChatView('chat');
      return;
    }
    if (lastInternalChatPedido) {
      setInternalChatPedidoId(lastInternalChatPedido.id);
      setInternalChatView('chat');
    }
  }

  function handleInternalChatTabPaciente() {
    goToInternalChatPickerView();
  }

  function handleInternalChatTabChat() {
    goToInternalChatConversationView();
  }

  function closeInternalChat() {
    setInternalChatPanelOpen(false);
    setInternalChatPedidoId(null);
    setInternalChatView('chat');
    setInternalChatPickerSearch('');
  }

  function clearToastTimer(id) {
    const timerId = toastTimersRef.current.get(id);
    if (timerId) {
      clearTimeout(timerId);
      toastTimersRef.current.delete(id);
    }
  }

  function dismissToast(id) {
    clearToastTimer(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
    setToastSwipeOffsets((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function scheduleToastRemoval(id, delay = TOAST_LIFETIME_MS) {
    clearToastTimer(id);
    const timerId = setTimeout(() => {
      toastTimersRef.current.delete(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, delay);
    toastTimersRef.current.set(id, timerId);
  }

  function toast(payload, type = 'info') {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, ...payload }]);
    scheduleToastRemoval(id);
  }

  function focusPedidoFromToast(toastItem) {
    const pedidoId = toastItem?.pedidoId;
    if (pedidoId == null || pedidoId === '') return;
    if ((toastSwipeOffsets[toastItem.id] || 0) > 12) return;
    pendingToastScrollPedidoIdRef.current = String(pedidoId);
    setSearch('');
    setExpandedPedidoId(pedidoId);
    scheduleToastRemoval(toastItem.id, TOAST_LIFETIME_MS);
  }

  function handleToastPointerDown(event, toastId) {
    toastGestureRef.current = {
      id: toastId,
      startX: event.clientX,
      pointerId: event.pointerId,
      dragging: false
    };
  }

  function handleToastPointerMove(event, toastId) {
    const gesture = toastGestureRef.current;
    if (gesture.id !== toastId || gesture.pointerId !== event.pointerId) return;
    const deltaX = Math.max(0, event.clientX - gesture.startX);
    if (deltaX > 0 && !gesture.dragging) {
      gesture.dragging = true;
    }
    setToastSwipeOffsets((prev) => {
      if ((prev[toastId] || 0) === deltaX) return prev;
      return { ...prev, [toastId]: deltaX };
    });
  }

  function resetToastSwipe(toastId) {
    setToastSwipeOffsets((prev) => {
      if (!(toastId in prev)) return prev;
      const next = { ...prev };
      delete next[toastId];
      return next;
    });
  }

  function finishToastSwipe(event, toastId) {
    const gesture = toastGestureRef.current;
    if (gesture.id !== toastId || (event && gesture.pointerId !== event.pointerId)) return;
    const deltaX = toastSwipeOffsets[toastId] || 0;
    toastGestureRef.current = { id: null, startX: 0, pointerId: null, dragging: false };
    if (deltaX >= 88) {
      dismissToast(toastId);
      return;
    }
    resetToastSwipe(toastId);
  }

  function openMedicationDrawer(pedido) {
    setMedicationDrawerPedido(pedido || null);
  }

  function closeMedicationDrawer() {
    setMedicationDrawerPedido(null);
  }

  const loadPedidos = async (signal) => {
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.pendientes}`, {
        credentials: 'include',
        signal
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (Array.isArray(json?.pedidos) ? json.pedidos : []);
      const mapped = list.map((r) => {
        const med = normalizeMedicationFields(r);
        const origenParsed = parsePedidoIngresoOrigen(r.notas);
        const ingresoOrigen = r.ingreso_origen || origenParsed.ingreso_origen || null;
        return {
          id: r.id,
          solicitante_dni: r.solicitante_dni,
          paciente_dni: r.paciente_dni,
          paciente: r.paciente_nombre || r.paciente || r.paciente_dni || r.solicitante_dni || 'Paciente',
          dni: r.paciente_dni || r.solicitante_dni || '',
          obra_social: r.obra_social_descripcio || r.obra_social || r.os_sigla_geclisa || '',
          medicamento_id: r.medicamento_id,
          ...med,
          medicamento_nombre: med.medicamento_nombre || (r.medicamento_id ? 'Cargando medicamento...' : ''),
          fecha_solicitud: r.fecha_solicitud,
          solicitado: r.fecha_solicitud ? formatSolicitud(r.fecha_solicitud) : '',
          notas: resolvePedidoNotasDisplay(r.notas, ingresoOrigen),
          ingreso_origen: ingresoOrigen,
          ingreso_conversation_id: r.ingreso_conversation_id || origenParsed.ingreso_conversation_id || null,
          message_history: r.message_history || '',
          es_externo: r.es_externo === 1 || r.es_externo === true,
          cliente_externo_id: r.cliente_externo_id ?? null
        };
      });

      if (signal?.aborted) return;

      const previousIds = knownPedidoIdsRef.current;
      const nuevos = pedidosLoadedRef.current
        ? mapped.filter((p) => !previousIds.has(String(p.id)))
        : [];

      if (signal?.aborted) return;
      setPedidos(mapped);
      knownPedidoIdsRef.current = new Set(mapped.map((p) => String(p.id)));
      pedidosLoadedRef.current = true;

      if (nuevos.length > 0) {
        const first = nuevos[0];
        const extra = nuevos.length > 1 ? ` y ${nuevos.length - 1} más` : '';
        toast({
          pedidoId: first.id,
          title: `Nuevo pedido de derivación${extra}`,
          paciente: first.paciente || 'Paciente',
          subtitle: `DNI ${formatDniDisplay(first.dni)}${first.medicamento_nombre ? ` · ${first.medicamento_nombre}` : ''}`
        });
      }

      // Resolver detalle enriquecido de medicamentos cuando el listado viene incompleto
      const unknownIds = Array.from(new Set(
        mapped
          .filter((p) => needsMedicationEnrichment(p) || p.medicamento_nombre === 'Cargando medicamento...')
          .map((p) => Number(p.medicamento_id))
          .filter((id) => Number.isFinite(id) && id > 0)
      ));
      unknownIds.forEach(async (mid) => {
        if (medicamentoCacheRef.current.has(mid)) {
          const cached = medicamentoCacheRef.current.get(mid);
          if (!needsMedicationEnrichment({ medicamento_id: mid, ...cached })) {
            setPedidos((prev) => prev.map((p) =>
              Number(p.medicamento_id) === Number(mid)
                ? { ...p, ...mergeMedicationFields(p, cached) }
                : p
            ));
            return;
          }
        }
        try {
          const r = await fetch(`${API_BASE_URL}${ENDPOINTS.medicamento(mid)}`, { credentials: 'include' });
          if (!r.ok) return;
          const jm = await r.json();
          const enriched = normalizeMedicationFields(jm);
          medicamentoCacheRef.current.set(mid, enriched);
          setPedidos((prev) => prev.map((p) =>
            Number(p.medicamento_id) === Number(mid)
              ? { ...p, ...mergeMedicationFields(p, enriched) }
              : p
          ));
        } catch (_) { /* ignore */ }
      });
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error('Error cargando pedidos pendientes de derivación:', err);
      }
    } finally {
      if (signal && !signal.aborted) setLoading(false);
    }
  };

  const loadMedicos = async (signal) => {
    setMedicosLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.medicos}`, {
        credentials: 'include',
        signal
      });
      if (!res.ok) return;
      const json = await res.json();
      if (signal?.aborted) return;
      setMedicos(normalizeMedicosFromApi(json));
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error('Error cargando médicos:', err);
      }
    } finally {
      if (signal && !signal.aborted) setMedicosLoading(false);
    }
  };

  const mapPedido = (r) => {
    const med = normalizeMedicationFields(r);
    const origenParsed = parsePedidoIngresoOrigen(r.notas);
    const ingresoOrigen = r.ingreso_origen || origenParsed.ingreso_origen || null;
    return {
      id: r.id,
      solicitante_dni: r.solicitante_dni,
      paciente_dni: r.paciente_dni,
      paciente: r.paciente_nombre || r.paciente || r.paciente_dni || r.solicitante_dni || 'Paciente',
      dni: r.paciente_dni || r.solicitante_dni || '',
      obra_social: r.obra_social_descripcio || r.obra_social || r.os_sigla_geclisa || '',
      medicamento_id: r.medicamento_id,
      ...med,
      fecha_solicitud: r.fecha_solicitud,
      solicitado: r.fecha_solicitud ? formatSolicitud(r.fecha_solicitud) : '',
      estado: r.estado || '',
      medico_id: r.medico_id,
      medico: r.medico || r.prestador_geclisa || '',
      notas: resolvePedidoNotasDisplay(r.notas, ingresoOrigen),
      ingreso_origen: ingresoOrigen,
      ingreso_conversation_id: r.ingreso_conversation_id || origenParsed.ingreso_conversation_id || null,
      message_history: r.message_history || '',
      es_externo: r.es_externo === 1 || r.es_externo === true,
      cliente_externo_id: r.cliente_externo_id ?? null
    };
  };

  const loadHistorial = async () => {
    setHistorialLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}${ENDPOINTS.historial}?gestion_derivacion_full=1`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (Array.isArray(json?.pedidos) ? json.pedidos : []);
      const mapped = list.map(mapPedido);
      setHistorialPedidos(mapped);
      const unknownIds = Array.from(new Set(
        mapped
          .filter((p) => needsMedicationEnrichment(p))
          .map((p) => Number(p.medicamento_id))
          .filter((id) => Number.isFinite(id) && id > 0)
      ));
      unknownIds.forEach(async (mid) => {
        if (medicamentoCacheRef.current.has(mid)) {
          const cached = medicamentoCacheRef.current.get(mid);
          if (!needsMedicationEnrichment({ medicamento_id: mid, ...cached })) {
            setHistorialPedidos((prev) => prev.map((p) =>
              Number(p.medicamento_id) === Number(mid) ? { ...p, ...mergeMedicationFields(p, cached) } : p
            ));
            return;
          }
        }
        try {
          const r = await fetch(`${API_BASE_URL}${ENDPOINTS.medicamento(mid)}`, { credentials: 'include' });
          if (!r.ok) return;
          const jm = await r.json();
          const enriched = normalizeMedicationFields(jm);
          medicamentoCacheRef.current.set(mid, enriched);
          setHistorialPedidos((prev) => prev.map((p) =>
            Number(p.medicamento_id) === Number(mid) ? { ...p, ...mergeMedicationFields(p, enriched) } : p
          ));
        } catch (_) { /* ignore */ }
      });
      setHistorialDrafts((prev) => {
        const next = { ...prev };
        mapped.forEach((p) => {
          if (!next[p.id]) {
            next[p.id] = {
              medico_id: p.medico_id ? String(p.medico_id) : '',
              notas: p.notas || ''
            };
          }
        });
        return next;
      });
    } catch (err) {
      console.error('Error cargando historial de derivaciones:', err);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo cargar el historial',
        text: err.message || 'Error desconocido'
      });
    } finally {
      setHistorialLoading(false);
    }
  };

  const openHistorial = () => {
    setHistorialOpen(true);
    setMedicoVistaOpen(false);
    setHistorialPage(1);
    loadHistorial();
  };

  const openMedicosStats = () => {
    setMedicosStatsOpen(true);
    loadHistorial();
  };

  const openMedicoVista = () => {
    historialVisibleBeforeMedicoRef.current = historialOpen;
    setMedicoVistaMedicoListOpen(false);
    setMedicoVistaOpen(true);
    setMedicoVistaBuscar('');
    setMedicoVistaMedicoFiltro('');
    loadHistorial();
  };

  const closeMedicoVista = () => {
    setMedicoVistaMedicoListOpen(false);
    setMedicoVistaOpen(false);
    if (restoreStatsOnMedicoVistaClose) {
      setRestoreStatsOnMedicoVistaClose(false);
      setMedicosStatsOpen(true);
      return;
    }
    if (historialVisibleBeforeMedicoRef.current) setHistorialOpen(true);
  };

  const openMedicoVistaFromStats = (medicoId) => {
    const id = String(medicoId || '').trim();
    if (!id) return;
    setMedicosStatsOpen(false);
    setRestoreStatsOnMedicoVistaClose(true);
    historialVisibleBeforeMedicoRef.current = historialOpen;
    setMedicoVistaMedicoListOpen(false);
    setMedicoVistaOpen(true);
    setMedicoVistaBuscar('');
    setMedicoVistaMedicoFiltro('');
    setMedicoVistaMedicoId(id);
    loadHistorial();
  };

  useEffect(() => {
    if (!medicoVistaOpen) setMedicoVistaMedicoListOpen(false);
  }, [medicoVistaOpen]);

  useEffect(() => {
    if (!medicoVistaMedicoListOpen) return undefined;
    const onDoc = (e) => {
      const root = medicoVistaMedicoPickerRef.current;
      if (root && !root.contains(e.target)) setMedicoVistaMedicoListOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [medicoVistaMedicoListOpen]);

  useEffect(() => {
    if (derivMedicoDropdownPedidoId == null) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const openId = derivMedicoDropdownPedidoId;
      const root = document.querySelector(`[data-deriv-medico-picker="${openId}"]`);
      if (root && !root.contains(t)) setDerivMedicoDropdownPedidoId(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [derivMedicoDropdownPedidoId]);

  useEffect(() => {
    if (historialMedicoDropdownPedidoId == null) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const openId = historialMedicoDropdownPedidoId;
      const root = document.querySelector(`[data-historial-medico-picker="${openId}"]`);
      if (root && !root.contains(t)) setHistorialMedicoDropdownPedidoId(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [historialMedicoDropdownPedidoId]);

  useEffect(() => {
    if (derivMedicoDropdownPedidoId == null) return;
    if (String(expandedPedidoId) !== String(derivMedicoDropdownPedidoId)) {
      setDerivMedicoDropdownPedidoId(null);
    }
  }, [expandedPedidoId, derivMedicoDropdownPedidoId]);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;
    setLoading(true);
    setMedicosLoading(true);

    const safeLoadPedidos = () => loadPedidos(signal).catch((err) => {
      if (err?.name !== 'AbortError' && !signal.aborted) {
        console.error('Error cargando pedidos pendientes de derivación:', err);
      }
    });
    const safeLoadMedicos = () => loadMedicos(signal).catch((err) => {
      if (err?.name !== 'AbortError' && !signal.aborted) {
        console.error('Error cargando médicos:', err);
      }
    });

    void safeLoadPedidos();
    void safeLoadMedicos();

    return () => ac.abort();
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      loadPedidos();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setToasts((prev) => prev.slice(-5));
    }, 3000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!cancelled) void loadHistorial();
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!internalChatPanelOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeInternalChat();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [internalChatPanelOpen]);

  useEffect(() => () => {
    toastTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    toastTimersRef.current.clear();
  }, []);

  useEffect(() => {
    setToastSwipeOffsets((prev) => {
      const visibleIds = new Set(toasts.map((toastItem) => String(toastItem.id)));
      const staleIds = Object.keys(prev).filter((id) => !visibleIds.has(String(id)));
      if (staleIds.length === 0) return prev;
      const next = { ...prev };
      staleIds.forEach((id) => delete next[id]);
      return next;
    });
  }, [toasts]);

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return pedidos;
    return pedidos.filter((p) => {
      const haystack = [
        p.paciente,
        p.dni,
        p.solicitante_dni,
        p.paciente_dni,
        p.obra_social,
        p.medicamento_nombre,
        p.nombre_generico,
        p.nombre_comercial
      ].map((v) => String(v || '').toLowerCase()).join(' | ');
      return haystack.includes(q);
    });
  }, [pedidos, search]);

  useEffect(() => {
    if (filtered.length === 0) {
      setExpandedPedidoId(null);
      return;
    }
    setExpandedPedidoId((prev) => {
      if (prev != null && filtered.some((p) => String(p.id) === String(prev))) {
        return prev;
      }
      return filtered[0].id;
    });
  }, [filtered]);

  useEffect(() => {
    const pendingPedidoId = pendingToastScrollPedidoIdRef.current;
    if (!pendingPedidoId) return;
    const pedidoNode = pedidoCardRefs.current[pendingPedidoId];
    if (!pedidoNode) return;
    pendingToastScrollPedidoIdRef.current = null;
    requestAnimationFrame(() => {
      pedidoNode.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    });
  }, [expandedPedidoId, filtered]);

  const historialPacientes = useMemo(() => {
    const map = new Map();
    historialPedidos.forEach((p) => {
      const dni = String(p.dni || '').replace(/\D/g, '').replace(/^0+/, '');
      if (!dni || map.has(dni)) return;
      map.set(dni, {
        dni,
        paciente: p.paciente || 'Paciente',
        obra_social: p.obra_social || ''
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.paciente).localeCompare(String(b.paciente), 'es', { sensitivity: 'base' })
    );
  }, [historialPedidos]);

  const altaRapidaPacientes = useMemo(() => {
    const map = new Map();
    [...pedidos, ...historialPedidos].forEach((p) => {
      const dni = String(p.dni || '').replace(/\D/g, '').replace(/^0+/, '');
      if (!dni) return;
      if (!map.has(dni)) {
        map.set(dni, {
          dni,
          paciente: p.paciente || 'Paciente',
          obra_social: p.obra_social || ''
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.paciente).localeCompare(String(b.paciente), 'es', { sensitivity: 'base' })
    );
  }, [pedidos, historialPedidos]);

  const filteredHistorialPacientes = useMemo(() => {
    const q = String(historialPacienteSearch || '').trim().toLowerCase();
    if (!q) return historialPacientes;
    return historialPacientes.filter((p) => (
      String(p.paciente || '').toLowerCase().includes(q)
      || String(p.dni || '').toLowerCase().includes(q)
      || String(p.obra_social || '').toLowerCase().includes(q)
    ));
  }, [historialPacientes, historialPacienteSearch]);

  useEffect(() => {
    const digits = String(historialPacienteSearch || '').replace(/\D/g, '').replace(/^0+/, '');
    if (!digits) return;
    const match = historialPacientes.find((p) => String(p.dni) === digits);
    if (match && String(historialDni) !== String(match.dni)) {
      setHistorialDni(match.dni);
      setHistorialPage(1);
    }
  }, [historialPacienteSearch, historialPacientes, historialDni]);

  const filteredHistorial = useMemo(() => {
    const q = String(historialSearch || '').trim().toLowerCase();
    const normalizeDate = (value) => {
      const raw = String(value || '').trim();
      const m = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
      return m ? m[0] : '';
    };
    if (!historialDni) return [];
    let rows = historialPedidos
      .filter((p) => p.estado !== 'pendiente_derivacion')
      .filter((p) => String(p.dni || '').replace(/\D/g, '').replace(/^0+/, '') === String(historialDni));
    if (historialEstado !== 'todos') {
      rows = rows.filter((p) => String(p.estado || '') === String(historialEstado));
    }
    if (historialDesde) {
      rows = rows.filter((p) => {
        const fecha = normalizeDate(p.fecha_solicitud);
        return fecha && fecha >= historialDesde;
      });
    }
    if (historialHasta) {
      rows = rows.filter((p) => {
        const fecha = normalizeDate(p.fecha_solicitud);
        return fecha && fecha <= historialHasta;
      });
    }
    if (q) {
      rows = rows.filter((p) => {
        const haystack = [
          p.id,
          p.paciente,
          p.dni,
          p.solicitante_dni,
          p.paciente_dni,
          p.estado,
          p.medico,
          p.medicamento_nombre,
          p.notas
        ].map((v) => String(v || '').toLowerCase()).join(' | ');
        return haystack.includes(q);
      });
    }
    return [...rows].sort((a, b) => {
      const da = new Date(a.fecha_solicitud || 0).getTime() || 0;
      const db = new Date(b.fecha_solicitud || 0).getTime() || 0;
      return historialOrder === 'fecha_asc' ? da - db : db - da;
    });
  }, [historialPedidos, historialSearch, historialDni, historialEstado, historialDesde, historialHasta, historialOrder]);

  const totalHistorialPages = Math.max(1, Math.ceil(filteredHistorial.length / historialItemsPerPage));
  const pageHistorial = filteredHistorial.slice(
    (historialPage - 1) * historialItemsPerPage,
    historialPage * historialItemsPerPage
  );

  const pedidosPorMedicoSeleccion = useMemo(() => {
    if (!medicoVistaMedicoId) return [];
    const medico = medicos.find((m) => String(m.id) === String(medicoVistaMedicoId));
    if (!medico) return [];
    const q = String(medicoVistaBuscar || '').trim().toLowerCase();
    let rows = historialPedidos.filter((p) => {
      if (String(p.estado) === 'pendiente_derivacion') return false;
      return pedidoAsignadoAMedico(p, medico);
    });
    if (q) {
      rows = rows.filter((p) => {
        const haystack = [
          p.paciente,
          p.dni,
          p.medicamento_nombre,
          p.estado,
          p.notas,
          p.medico
        ]
          .map((v) => String(v || '').toLowerCase())
          .join(' | ');
        return haystack.includes(q);
      });
    }
    return [...rows].sort((a, b) => {
      const da = new Date(a.fecha_solicitud || 0).getTime() || 0;
      const db = new Date(b.fecha_solicitud || 0).getTime() || 0;
      const pa = String(a.paciente || '').toLowerCase();
      const pb = String(b.paciente || '').toLowerCase();
      if (medicoVistaListaOrder === 'paciente_asc') return pa.localeCompare(pb, 'es', { sensitivity: 'base' }) || (db - da);
      if (medicoVistaListaOrder === 'paciente_desc') return pb.localeCompare(pa, 'es', { sensitivity: 'base' }) || (db - da);
      if (medicoVistaListaOrder === 'fecha_asc') return da - db;
      return db - da;
    });
  }, [historialPedidos, medicoVistaMedicoId, medicos, medicoVistaBuscar, medicoVistaListaOrder]);

  /** Totales por médico en el historial cargado (derivados ya asignados, sin pendiente_derivacion). */
  const tratamientosCountByMedicoId = useMemo(() => {
    const map = new Map();
    (medicos || []).forEach((m) => {
      const n = (historialPedidos || []).filter((p) => {
        if (String(p.estado) === 'pendiente_derivacion') return false;
        return pedidoAsignadoAMedico(p, m);
      }).length;
      map.set(String(m.id), n);
    });
    return map;
  }, [historialPedidos, medicos]);

  const medicosStatsRows = useMemo(() => {
    return (medicos || [])
      .map((m) => {
        const total = Number(tratamientosCountByMedicoId.get(String(m.id)) || 0);
        return {
          id: String(m.id),
          nombre: m.nombre,
          especialidad: m.especialidad || '',
          matricula: m.matricula || '',
          total
        };
      })
      .filter((r) => r.total > 0)
      .sort((a, b) => b.total - a.total || String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' }));
  }, [medicos, tratamientosCountByMedicoId]);

  const medicosConTratamientos = medicosStatsRows.length;
  const medicosSinTratamientos = Math.max(0, medicos.length - medicosConTratamientos);
  const maxTratamientosMedico = medicosStatsRows[0]?.total || 0;
  const totalTratamientosAcumulados = medicosStatsRows.reduce((acc, r) => acc + r.total, 0);
  const promedioTratamientosActivo = medicosConTratamientos > 0 ? (totalTratamientosAcumulados / medicosConTratamientos) : 0;
  const topCincoTratamientos = medicosStatsRows.slice(0, 5).reduce((acc, r) => acc + r.total, 0);
  const topCincoCargaPct = totalTratamientosAcumulados > 0 ? Math.round((topCincoTratamientos / totalTratamientosAcumulados) * 100) : 0;
  const medicosConUnaCarga = medicosStatsRows.filter((r) => r.total === 1).length;
  const medianaTratamientosMedico = (() => {
    if (medicosStatsRows.length === 0) return 0;
    const totals = medicosStatsRows.map((r) => r.total).sort((a, b) => a - b);
    const mid = Math.floor(totals.length / 2);
    return totals.length % 2 === 0 ? ((totals[mid - 1] + totals[mid]) / 2) : totals[mid];
  })();
  const medicosStatsItemsPerPage = 10;
  const medicosStatsFiltered = useMemo(() => {
    const q = String(medicosStatsSearch || '').trim().toLowerCase();
    if (!q) return medicosStatsRows;
    return medicosStatsRows.filter((row) => {
      const shown = formatDoctorDisplayName(row.nombre).toLowerCase();
      const nombre = String(row.nombre || '').toLowerCase();
      const especialidad = String(row.especialidad || '').toLowerCase();
      const matricula = String(row.matricula || '').toLowerCase();
      return shown.includes(q) || nombre.includes(q) || especialidad.includes(q) || matricula.includes(q);
    });
  }, [medicosStatsRows, medicosStatsSearch]);
  const totalMedicosStatsPages = Math.max(1, Math.ceil(medicosStatsFiltered.length / medicosStatsItemsPerPage));
  const medicosStatsTop = medicosStatsFiltered.slice(
    (medicosStatsPage - 1) * medicosStatsItemsPerPage,
    medicosStatsPage * medicosStatsItemsPerPage
  );
  const statsIsMobile = window.innerWidth <= 768;
  const statsIsCompact = window.innerWidth <= 1100;
  const derivCardHeaderIsMobile = window.innerWidth <= 640;
  const totalMedicosEnGrafico = Math.max(1, medicosConTratamientos + medicosSinTratamientos);
  const medicosActivosPct = Math.round((medicosConTratamientos / totalMedicosEnGrafico) * 100);
  const promedioTratamientosActivoLabel = promedioTratamientosActivo.toLocaleString('es-AR', { minimumFractionDigits: promedioTratamientosActivo % 1 === 0 ? 0 : 1, maximumFractionDigits: 1 });
  const medianaTratamientosMedicoLabel = medianaTratamientosMedico.toLocaleString('es-AR', { minimumFractionDigits: medianaTratamientosMedico % 1 === 0 ? 0 : 1, maximumFractionDigits: 1 });
  const donutRadius = 58;
  const donutCircumference = 2 * Math.PI * donutRadius;
  const donutActiveLen = (medicosConTratamientos / totalMedicosEnGrafico) * donutCircumference;

  const medicosParaSelectorMedicoVista = useMemo(() => {
    const qLista = String(medicoVistaMedicoFiltro || '').trim().toLowerCase();
    let list = medicos;
    if (qLista) {
      list = medicos.filter((m) => {
        const nombre = String(m.nombre || '').toLowerCase();
        const esp = String(m.especialidad || '').toLowerCase();
        const mat = String(m.matricula || '').toLowerCase();
        const shown = formatDoctorDisplayName(m.nombre).toLowerCase();
        return nombre.includes(qLista) || shown.includes(qLista) || esp.includes(qLista) || mat.includes(qLista);
      });
    }
    if (medicoVistaMedicoId) {
      const hasSel = list.some((m) => String(m.id) === String(medicoVistaMedicoId));
      if (!hasSel) {
        const sel = medicos.find((m) => String(m.id) === String(medicoVistaMedicoId));
        if (sel) list = [sel, ...list];
      }
    }
    return [...list].sort((a, b) => {
      const aCount = Number(tratamientosCountByMedicoId.get(String(a.id)) || 0);
      const bCount = Number(tratamientosCountByMedicoId.get(String(b.id)) || 0);
      const aName = String(formatDoctorDisplayName(a.nombre || '')).toLowerCase();
      const bName = String(formatDoctorDisplayName(b.nombre || '')).toLowerCase();
      if (medicoVistaMedicoOrder === 'nombre_asc') return aName.localeCompare(bName, 'es', { sensitivity: 'base' }) || (bCount - aCount);
      if (medicoVistaMedicoOrder === 'nombre_desc') return bName.localeCompare(aName, 'es', { sensitivity: 'base' }) || (bCount - aCount);
      if (medicoVistaMedicoOrder === 'badge_asc') return aCount - bCount || aName.localeCompare(bName, 'es', { sensitivity: 'base' });
      return bCount - aCount || aName.localeCompare(bName, 'es', { sensitivity: 'base' });
    });
  }, [medicos, medicoVistaMedicoFiltro, medicoVistaMedicoId, medicoVistaMedicoOrder, tratamientosCountByMedicoId]);

  useEffect(() => {
    if (historialPage > totalHistorialPages) setHistorialPage(1);
  }, [historialPage, totalHistorialPages]);

  useEffect(() => {
    if (medicosStatsPage > totalMedicosStatsPages) setMedicosStatsPage(1);
  }, [medicosStatsPage, totalMedicosStatsPages]);

  async function guardarHistorialPedido(pedido) {
    const draft = historialDrafts[pedido.id] || {};
    const medicoSeleccionado = medicos.find((m) => String(m.id) === String(draft.medico_id));
    setHistorialSaving((prev) => ({ ...prev, [pedido.id]: true }));

    const numericId = Number(draft.medico_id);
    const body = {
      ...(medicoSeleccionado && Number.isFinite(numericId) && numericId > 0 ? { medico_id: numericId } : {}),
      ...(medicoSeleccionado ? {
        medico: medicoSeleccionado.nombre,
        medico_especialidad: medicoSeleccionado.especialidad || null,
        medico_matricula: medicoSeleccionado.matricula || null
      } : {}),
      notas: draft.notas || ''
    };

    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.actualizarDerivacion(pedido.id)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      const updated = json.pedido || {};
      const medicoNombre = json.medico?.nombre || medicoSeleccionado?.nombre || pedido.medico || '';
      setHistorialPedidos((prev) => prev.map((p) => (
        p.id === pedido.id
          ? { ...p, medico_id: updated.medico_id ?? p.medico_id, medico: medicoNombre, notas: updated.notas ?? body.notas }
          : p
      )));
      setPedidos((prev) => prev.map((p) => (
        p.id === pedido.id
          ? { ...p, medico_id: updated.medico_id ?? p.medico_id, medico: medicoNombre, notas: updated.notas ?? body.notas }
          : p
      )));
      toast({
        title: 'Derivación actualizada',
        paciente: pedido.paciente,
        subtitle: medicoNombre ? `Médico: ${formatDoctorDisplayName(medicoNombre)}` : 'Nota actualizada'
      }, 'success');
    } catch (err) {
      console.error('Error guardando historial de derivación:', err);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo guardar',
        text: err.message || 'Error desconocido'
      });
    } finally {
      setHistorialSaving((prev) => {
        const next = { ...prev };
        delete next[pedido.id];
        return next;
      });
    }
  }

  async function enviarMensajeInternoHistorial(pedido) {
    const pedidoId = Number(pedido?.id);
    const mensaje = String(internalMessageDrafts[pedidoId] || '').trim();
    if (!pedidoId || !mensaje) {
      Swal.fire({ icon: 'warning', title: 'Mensaje vacío', text: 'Escribe un mensaje interno breve antes de enviarlo.' });
      return;
    }

    setInternalMessageSaving((prev) => ({ ...prev, [pedidoId]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.mensajeInterno(pedidoId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mensaje })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      const updated = json.pedido || {};
      setHistorialPedidos((prev) => prev.map((item) => (
        Number(item.id) === pedidoId ? { ...item, message_history: updated.message_history ?? item.message_history } : item
      )));
      setPedidos((prev) => prev.map((item) => (
        Number(item.id) === pedidoId ? { ...item, message_history: updated.message_history ?? item.message_history } : item
      )));
      setInternalMessageDrafts((prev) => ({ ...prev, [pedidoId]: '' }));
      toast({
        title: 'Mensaje interno enviado',
        paciente: pedido.paciente,
        subtitle: 'Disponible para revisión del médico.'
      }, 'success');
    } catch (error) {
      console.error('Error enviando mensaje interno desde historial:', error);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo enviar',
        text: error.message || 'Error desconocido'
      });
    } finally {
      setInternalMessageSaving((prev) => ({ ...prev, [pedidoId]: false }));
    }
  }

  function handleAltaRapidaPedidoCreated(nuevoBase) {
    const med = normalizeMedicationFields(nuevoBase);
    const pedido = {
      id: nuevoBase.id,
      solicitante_dni: nuevoBase.solicitante_dni,
      paciente_dni: nuevoBase.paciente_dni,
      paciente: nuevoBase.paciente || 'Paciente',
      dni: nuevoBase.dni || nuevoBase.paciente_dni || nuevoBase.solicitante_dni || '',
      obra_social: nuevoBase.obra_social || '',
      medicamento_id: nuevoBase.medicamento_id,
      ...med,
      medicamento_nombre: med.medicamento_nombre || nuevoBase.medicamento || (nuevoBase.medicamento_id ? 'Cargando medicamento...' : ''),
      fecha_solicitud: nuevoBase.fecha_solicitud,
      solicitado: nuevoBase.fecha_solicitud ? formatSolicitud(nuevoBase.fecha_solicitud) : (nuevoBase.solicitado || ''),
      notas: '',
      ingreso_origen: null,
      ingreso_conversation_id: null,
      message_history: ''
    };

    setPedidos((prev) => [pedido, ...prev.filter((p) => Number(p.id) !== Number(pedido.id))]);
    knownPedidoIdsRef.current.add(String(pedido.id));
    setModalAltaRapidaOpen(false);
    setExpandedPedidoId(pedido.id);
    setSearch('');

    const mid = Number(pedido.medicamento_id);
    if (Number.isFinite(mid) && mid > 0 && needsMedicationEnrichment(pedido)) {
      (async () => {
        try {
          const r = await fetch(`${API_BASE_URL}${ENDPOINTS.medicamento(mid)}`, { credentials: 'include' });
          if (!r.ok) return;
          const jm = await r.json();
          const enriched = normalizeMedicationFields(jm);
          medicamentoCacheRef.current.set(mid, enriched);
          setPedidos((prev) => prev.map((p) => (
            Number(p.id) === Number(pedido.id) ? { ...p, ...mergeMedicationFields(p, enriched) } : p
          )));
        } catch (_) { /* ignore */ }
      })();
    }

    toast({
      pedidoId: pedido.id,
      title: 'Pedido creado — pendiente de derivación',
      paciente: pedido.paciente,
      subtitle: `DNI ${formatDniDisplay(pedido.dni)}${pedido.medicamento_nombre ? ` · ${pedido.medicamento_nombre}` : ''}`
    }, 'success');
  }

  async function handleDerivar(pedido) {
    const medicoIdRaw = seleccion[pedido.id];
    const medicoSeleccionado = medicos.find((m) => String(m.id) === String(medicoIdRaw));

    if (!medicoSeleccionado) {
      Swal.fire({
        icon: 'warning',
        title: 'Falta médico',
        text: 'Seleccioná un médico para derivar el pedido.',
        timer: 2400,
        showConfirmButton: false
      });
      return;
    }

    const confirm = await fireConfirmDialog({
      title: 'Derivar pedido',
      html: buildCrmSwalBody({
        lead: 'Revisá los datos antes de enviar al médico:',
        details: [
          { label: 'Paciente', value: pedido.paciente || '—' },
          { label: 'DNI', value: formatDniDisplay(pedido.dni) || '—' },
          { label: 'Medicamento', value: pedido.medicamento_nombre || '—' },
          { label: 'Médico', value: formatDoctorDisplayName(medicoSeleccionado.nombre) || '—' }
        ]
      }),
      confirmButtonText: 'Derivar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirm.isConfirmed) return;

    setSubmitting((prev) => ({ ...prev, [pedido.id]: true }));

    const numericId = Number(medicoSeleccionado.id);
    const body = {
      // Si el id es numérico, lo enviamos. Si es sintético (geclisa-xxx),
      // mandamos el nombre para que el backend lo resuelva o lo cree.
      ...(Number.isFinite(numericId) && numericId > 0 ? { medico_id: numericId } : {}),
      medico: medicoSeleccionado.nombre,
      medico_especialidad: medicoSeleccionado.especialidad || null,
      medico_matricula: medicoSeleccionado.matricula || null,
      notas: (notas[pedido.id] || '').trim() || undefined
    };

    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.derivar(pedido.id)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.message || `HTTP ${res.status}`);
      }
      setPedidos((prev) => prev.filter((p) => p.id !== pedido.id));
      setSeleccion((prev) => {
        const next = { ...prev };
        delete next[pedido.id];
        return next;
      });
      setNotas((prev) => {
        const next = { ...prev };
        delete next[pedido.id];
        return next;
      });
      setDerivMedicoDropdownPedidoId((id) => (id != null && String(id) === String(pedido.id) ? null : id));
      if (json?.pedido) {
        dispatchPedidoDerivadoAlMedico({ pedido: json.pedido, medico: json.medico });
      }
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: 'Pedido derivado al médico',
        showConfirmButton: false,
        timer: 2200,
        timerProgressBar: true
      });
    } catch (err) {
      console.error('Error derivando pedido:', err);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo derivar el pedido',
        text: err.message || 'Error desconocido al derivar el pedido.'
      });
    } finally {
      setSubmitting((prev) => {
        const next = { ...prev };
        delete next[pedido.id];
        return next;
      });
    }
  }

  return (
    <section className="crm-gd-page crm-gd-deriv-page crm-pbody">
      <div className="crm-gd-deriv-shell">
        <div className="crm-gd-deriv-top">
          <div className="crm-gd-kpi-grid">
            <div className="crm-gd-kpi-card">
              <div className="crm-gd-kpi-card__num">{pedidos.length}</div>
              <div className="crm-gd-kpi-card__label">Pendientes de derivación</div>
            </div>
            <div className="crm-gd-kpi-card">
              <div className="crm-gd-kpi-card__num">{medicos.length}</div>
              <div className="crm-gd-kpi-card__label">Médicos disponibles</div>
            </div>
          </div>

          <div className="crm-gd-toolbar">
            <div className="crm-gd-toolbar__search">
              <CwListSearch
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar paciente, DNI o medicamento…"
              />
            </div>
            <div className="crm-gd-toolbar__actions">
              <button
                type="button"
                className="crm-gd-btn"
                onClick={openMedicosStats}
                title="Abrir analítica de médicos"
              >
                <span aria-hidden>📊</span>
                Médicos con tratamiento
              </button>
              {canAltaRapida ? (
                <button
                  type="button"
                  className="crm-gd-btn crm-gd-btn--ghost"
                  onClick={() => setModalAltaRapidaOpen(true)}
                >
                  <span aria-hidden>＋</span>
                  Alta rápida
                </button>
              ) : null}
              <button
                type="button"
                className="crm-gd-btn crm-gd-btn--ghost"
                onClick={openHistorial}
              >
                Historial global
              </button>
            </div>
          </div>
        </div>

        <div className="crm-gd-deriv-scroll">
      {loading ? (
        <div className="crm-gd-loading" role="status" aria-live="polite">
          <div className="crm-gd-spinner" aria-hidden="true" />
          <div className="crm-gd-loading__title">Preparando gestión de derivación</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Cargando pedidos pendientes…
          </div>
        </div>
      ) : pedidos.length === 0 ? (
        <div className="crm-gd-empty">
          No hay pedidos pendientes de derivación en este momento.
        </div>
      ) : filtered.length === 0 ? (
        <div className="crm-gd-empty">
          No se encontraron coincidencias para &quot;<b>{search}</b>&quot;.
        </div>
      ) : (
        <div className="crm-gd-list">
          {filtered.map((p) => {
            const medicoIdSeleccionado = seleccion[p.id] ?? '';
            const isSubmitting = !!submitting[p.id];
            const canSubmit = Boolean(medicoIdSeleccionado) && !isSubmitting;
            const isExpanded = String(expandedPedidoId) === String(p.id);
            const medicationDisplay = buildMedicationDisplay(p);
            const qDeriv = String(derivMedicoBuscar[p.id] || '').trim().toLowerCase();
            let medicosLista = medicos;
            if (qDeriv) {
              medicosLista = medicos.filter((m) => {
                const nombre = String(m.nombre || '').toLowerCase();
                const esp = String(m.especialidad || '').toLowerCase();
                const mat = String(m.matricula || '').toLowerCase();
                const shown = formatDoctorDisplayName(m.nombre).toLowerCase();
                return nombre.includes(qDeriv) || shown.includes(qDeriv) || esp.includes(qDeriv) || mat.includes(qDeriv);
              });
            }
            if (medicoIdSeleccionado) {
              const hasSel = medicosLista.some((m) => String(m.id) === String(medicoIdSeleccionado));
              if (!hasSel) {
                const sel = medicos.find((m) => String(m.id) === String(medicoIdSeleccionado));
                if (sel) medicosLista = [sel, ...medicosLista];
              }
            }
            return (
              <div
                key={p.id}
                ref={(el) => { pedidoCardRefs.current[p.id] = el; }}
                className={`crm-gd-card${String(derivMedicoDropdownPedidoId) === String(p.id) ? ' crm-gd-card--overflow-visible' : ''}`}
              >
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  className={`crm-gd-card__header${isExpanded ? ' crm-gd-card__header--expanded' : ''}`}
                  style={{
                    padding: derivCardHeaderIsMobile ? '10px 10px 10px 14px' : undefined,
                    alignItems: derivCardHeaderIsMobile ? 'flex-start' : undefined
                  }}
                  onClick={() => setExpandedPedidoId((prev) => (prev === p.id ? null : p.id))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedPedidoId((prev) => (prev === p.id ? null : p.id));
                    }
                  }}
                >
                  <div className="crm-gd-card__patient" style={{ gap: derivCardHeaderIsMobile ? 10 : 12 }}>
                    <div className="crm-gd-avatar">{patientInitials(p.paciente)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div className="crm-gd-card__name" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {p.paciente}
                        <PacienteExternoBadge show={p.es_externo} compact />
                      </div>
                      <div className="crm-gd-card__meta">
                        DNI {formatDniDisplay(p.dni)}
                        {p.obra_social ? ` · ${p.obra_social}` : ''}
                      </div>
                      {p.solicitado ? (
                        <div className="crm-gd-card__meta" style={{ marginTop: 2 }}>
                          Solicitado: {p.solicitado}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="crm-gd-card__badges" style={{ width: derivCardHeaderIsMobile ? '100%' : 'auto', justifyContent: derivCardHeaderIsMobile ? 'space-between' : 'flex-end' }}>
                    {p.ingreso_origen ? (
                      <PedidoIngresoOrigenBadge
                        origen={p.ingreso_origen}
                        conversationId={p.ingreso_conversation_id}
                        compact
                      />
                    ) : null}
                    <span className="crm-gd-pill crm-gd-pill--pending" style={{ maxWidth: derivCardHeaderIsMobile ? 'calc(100% - 46px)' : '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>Pendiente derivación</span>
                    <span
                      aria-hidden
                      className={`crm-gd-chevron${isExpanded ? ' crm-gd-chevron--expanded' : ''}`}
                    >
                      {isExpanded ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
                          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
                          <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" fill="currentColor" />
                        </svg>
                      )}
                    </span>
                  </div>
                </div>

                <div
                  ref={(el) => { derivCardBodyRefs.current[p.id] = el; }}
                  style={{
                    overflow: String(derivMedicoDropdownPedidoId) === String(p.id) ? 'visible' : 'hidden',
                    transition: 'max-height .38s cubic-bezier(.2,.9,.2,1), opacity .28s ease',
                    maxHeight: isExpanded
                      ? `${Math.max(derivCardBodyRefs.current[p.id]?.scrollHeight || 0, 10000)}px`
                      : '0px',
                    opacity: isExpanded ? 1 : 0
                  }}
                >
                  <div className="crm-gd-card__body">
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                        <div>
                          <div className="crm-gd-field-label">Medicamento solicitado</div>
                          <div className="crm-gd-med-box">
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                              <div style={{ ...styles.fieldValue, fontSize: 15, fontWeight: 800, lineHeight: 1.3, flex: 1, minWidth: 0, overflow: 'visible' }}>
                                {medicationDisplay.title}
                              </div>
                              {(() => {
                                const isMedicationDrawerOpen = String(medicationDrawerPedido?.id || '') === String(p?.id || '');
                                return (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (isMedicationDrawerOpen) {
                                        closeMedicationDrawer();
                                        return;
                                      }
                                      openMedicationDrawer(p);
                                    }}
                                    aria-label={isMedicationDrawerOpen ? 'Ocultar ficha del medicamento' : 'Ver ficha del medicamento'}
                                    title={isMedicationDrawerOpen ? 'Ocultar ficha' : 'Ver ficha'}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 30,
                                      height: 30,
                                      flexShrink: 0,
                                      borderRadius: 999,
                                      border: isMedicationDrawerOpen ? '1px solid rgba(29,78,216,.36)' : '1px solid rgba(37,99,235,.24)',
                                      background: isMedicationDrawerOpen ? 'linear-gradient(180deg,#dbeafe,#eff6ff)' : 'linear-gradient(180deg,#ffffff,#eff6ff)',
                                      color: '#1d4ed8',
                                      cursor: 'pointer',
                                      boxShadow: isMedicationDrawerOpen ? '0 8px 18px rgba(37,99,235,.18)' : 'none'
                                    }}
                                  >
                                    {renderEyeIcon(isMedicationDrawerOpen)}
                                  </button>
                                );
                              })()}
                            </div>
                            {medicationDisplay.genericLine || medicationDisplay.laboratoryLabel ? (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 10,
                                  flexWrap: 'wrap',
                                  marginTop: 4
                                }}
                              >
                                {medicationDisplay.genericLine ? (
                                  <div style={{ ...styles.metaRow, color: '#475569', minWidth: 0, flex: 1 }}>
                                    {medicationDisplay.genericLine}
                                  </div>
                                ) : <div style={{ flex: 1 }} />}
                                {medicationDisplay.laboratoryLabel ? (
                                  <div
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: '4px 8px',
                                      borderRadius: 999,
                                      border: '1px solid rgba(148,163,184,.22)',
                                      background: '#ffffff',
                                      color: '#64748b',
                                      fontSize: 10,
                                      fontWeight: 800,
                                      letterSpacing: '.04em',
                                      textTransform: 'uppercase',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {medicationDisplay.laboratoryLabel}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                            {medicationDisplay.meta.length > 0 ? (
                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 6,
                                  marginTop: 8
                                }}
                              >
                                {medicationDisplay.meta.map((item) => (
                                  <span
                                    key={`${p.id}-${item}`}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: '4px 8px',
                                      borderRadius: 999,
                                      border: '1px solid rgba(37,99,235,.16)',
                                      background: '#eff6ff',
                                      color: '#1e3a8a',
                                      fontSize: 11,
                                      fontWeight: 700,
                                      lineHeight: 1.2
                                    }}
                                  >
                                    {item}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            {medicationDisplay.detailLines.length > 0 ? (
                              <div style={{ display: 'grid', gap: 2, marginTop: 8 }}>
                                {medicationDisplay.detailLines.map((item) => (
                                  <div key={`${p.id}-detail-${item}`} style={{ ...styles.metaRow, color: '#475569' }}>
                                    {item}
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <PedidoDerivacionOrigenNotas pedido={p} />
                        <div style={{ ...styles.helpText, marginTop: 4 }}>
                          El rol administrativo tiene acceso restringido dentro del sistema: no está habilitado para modificar información clínica ni para completar o actualizar tratamientos. Estas acciones son exclusivas del profesional médico responsable.
                          La carga, edición y validación de los datos clínicos, así como la gestión del tratamiento, deberán ser realizadas por el médico derivado, quien podrá llevarlas a cabo desde el módulo correspondiente de Pedidos/Recetas. De este modo, se garantiza la integridad de la información y el cumplimiento de los criterios médicos establecidos.
                        </div>
                      </div>
                    </div>

                    <div className="crm-gd-deriv-box">
                      <div>
                        <div className="crm-gd-field-label">Derivar a médico</div>
                        <input
                          type="search"
                          value={derivMedicoBuscar[p.id] || ''}
                          onChange={(e) => setDerivMedicoBuscar((prev) => ({ ...prev, [p.id]: e.target.value }))}
                          placeholder="Buscar por nombre, especialidad o matrícula…"
                          disabled={isSubmitting}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Buscar médico para derivar"
                          className="crm-gd-input"
                          style={{
                            width: '100%',
                            maxWidth: 'none',
                            minWidth: 0,
                            flex: 'none',
                            marginBottom: 6
                          }}
                        />
                        {qDeriv ? (
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                            Mostrando <strong style={{ color: '#334155' }}>{medicosLista.length}</strong>
                            {' '}de {medicos.length} médicos
                          </div>
                        ) : null}
                        {qDeriv && medicosLista.length === 0 ? (
                          <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
                            No hay médicos que coincidan con la búsqueda. Probá con otro texto o borrá el filtro.
                          </div>
                        ) : null}
                        <div
                          data-deriv-medico-picker={p.id}
                          style={{ position: 'relative' }}
                        >
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isSubmitting) return;
                              setDerivMedicoDropdownPedidoId((cur) => (String(cur) === String(p.id) ? null : p.id));
                            }}
                            aria-expanded={String(derivMedicoDropdownPedidoId) === String(p.id)}
                            aria-haspopup="listbox"
                            style={{
                              width: '100%',
                              minHeight: 40,
                              padding: '6px 10px',
                              borderRadius: 8,
                              border: '1px solid #cbd5e1',
                              fontSize: 13,
                              background: isSubmitting ? '#f8fafc' : '#fff',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              opacity: isSubmitting ? 0.7 : 1,
                              boxSizing: 'border-box',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 10,
                              textAlign: 'left'
                            }}
                          >
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#253342', fontWeight: 600 }}>
                              {(() => {
                                const sel = medicos.find((mm) => String(mm.id) === String(medicoIdSeleccionado));
                                if (!sel) return '— Seleccionar médico —';
                                return `${formatDoctorDisplayName(sel.nombre)}${sel.especialidad ? ` — ${sel.especialidad}` : ''}`;
                              })()}
                            </span>
                            <span style={{ flexShrink: 0, color: '#94a3b8', fontSize: 12, fontWeight: 800 }}>▾</span>
                          </button>

                          {String(derivMedicoDropdownPedidoId) === String(p.id) && !isSubmitting ? (
                            <div
                              role="listbox"
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                marginTop: 4,
                                maxHeight: 280,
                                overflowY: 'auto',
                                zIndex: 40,
                                background: '#fff',
                                border: '1px solid rgba(148,163,184,.32)',
                                borderRadius: 10,
                                boxShadow: '0 14px 36px rgba(15,23,42,.14), 0 2px 8px rgba(15,23,42,.06)'
                              }}
                            >
                              <button
                                type="button"
                                role="option"
                                aria-selected={!medicoIdSeleccionado}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSeleccion((prev) => ({ ...prev, [p.id]: '' }));
                                  setDerivMedicoDropdownPedidoId(null);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '10px 12px',
                                  border: 'none',
                                  borderBottom: '1px solid #f1f5f9',
                                  background: !medicoIdSeleccionado ? '#eff6ff' : '#fff',
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  color: '#64748b',
                                  fontWeight: 700,
                                  textAlign: 'left',
                                  boxSizing: 'border-box'
                                }}
                              >
                                — Seleccionar médico —
                              </button>
                              {medicosLista.map((m) => {
                                const selected = String(medicoIdSeleccionado) === String(m.id);
                                return (
                                  <button
                                    key={`deriv-mv-${p.id}-${m.id}`}
                                    type="button"
                                    role="option"
                                    aria-selected={selected}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSeleccion((prev) => ({ ...prev, [p.id]: String(m.id) }));
                                      setDerivMedicoDropdownPedidoId(null);
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '10px 12px',
                                      border: 'none',
                                      borderBottom: '1px solid #f1f5f9',
                                      background: selected ? '#eff6ff' : '#fff',
                                      cursor: 'pointer',
                                      fontSize: 13,
                                      color: '#253342',
                                      textAlign: 'left',
                                      boxSizing: 'border-box'
                                    }}
                                  >
                                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                      {formatDoctorDisplayName(m.nombre)}
                                      {m.especialidad ? ` — ${m.especialidad}` : ''}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div>
                        <div className="crm-gd-field-label">Comentario para el médico (opcional)</div>
                        <textarea
                          className="crm-gd-textarea"
                          value={notas[p.id] || ''}
                          onChange={(e) =>
                            setNotas((prev) => ({ ...prev, [p.id]: e.target.value }))
                          }
                          placeholder="Ej: paciente crónico, urgencia, datos relevantes para la revisión clínica…"
                          style={{ width: '95%', resize: 'none' }}
                          disabled={isSubmitting}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <button
                        type="button"
                        className="crm-gd-btn crm-gd-btn--primary"
                        onClick={(e) => { e.stopPropagation(); handleDerivar(p); }}
                        disabled={!canSubmit}
                      >
                        {isSubmitting ? 'Derivando…' : 'Derivar al médico'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </div>
      </div>
      <div
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right${historialOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setHistorialOpen(false);
            setMedicoVistaOpen(false);
          }
        }}
      >
        <aside
          className={`crm-gd-drawer-panel${historialOpen ? ' crm-gd-drawer-panel--right-open' : ' crm-gd-drawer-panel--right-closed'}`}
          style={{
            width: window.innerWidth <= 768 ? '100vw' : '52vw',
            minWidth: window.innerWidth <= 768 ? 0 : 580,
            maxWidth: window.innerWidth <= 768 ? '100vw' : 920,
            padding: window.innerWidth <= 768 ? 12 : 20
          }}
        >
          <div className="crm-gd-drawer-head">
            <div style={{ minWidth: 0 }}>
              <div className="crm-gd-drawer-head__title">Historial global de derivaciones</div>
              <div className="crm-gd-drawer-head__sub">
                Consultá todos los pedidos y corregí médico o nota si hubo un error.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button type="button" className="crm-gd-btn crm-gd-btn--ghost" onClick={openMedicoVista}>
                Por médico
              </button>
              <button
                type="button"
                className="crm-gd-btn"
                onClick={() => {
                  setHistorialOpen(false);
                  setMedicoVistaOpen(false);
                }}
              >
                Cerrar
              </button>
            </div>
          </div>

          <div className="crm-gd-drawer-body">
            <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'minmax(210px, 1fr) minmax(260px, 1.35fr) auto', gap: 10, alignItems: 'end' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block', fontWeight: 700 }}>Paciente (DNI)</label>
                <input
                  value={historialPacienteSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setHistorialPacienteSearch(value);
                    const digits = String(value || '').replace(/\D/g, '').replace(/^0+/, '');
                    if (!digits) {
                      setHistorialDni('');
                      setHistorialPage(1);
                    }
                  }}
                  placeholder="Buscar paciente en el selector..."
                  className="crm-gd-input"
                  style={{ width: '100%', maxWidth: 'none', height: 36 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block', fontWeight: 700 }}>Seleccionar paciente</label>
                <select
                  value={historialDni}
                  onChange={(e) => { setHistorialDni(e.target.value); setHistorialPage(1); }}
                  className="crm-gd-select"
                  style={{ cursor: 'pointer', width: '100%', height: 36 }}
                >
                  <option value="">{filteredHistorialPacientes.length === 0 ? 'Sin coincidencias' : 'Seleccioná un paciente'}</option>
                  {filteredHistorialPacientes.map((p) => (
                    <option key={p.dni} value={p.dni}>
                      {p.paciente} · DNI {formatDniDisplay(p.dni)}{p.obra_social ? ` · ${p.obra_social}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <span className="crm-gd-filter-chip">
                {filteredHistorial.length} registros
              </span>
            </div>

            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'minmax(280px, 1.35fr) minmax(150px, .65fr)', gap: 10, alignItems: 'end' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Buscar en historial global</label>
                <input
                  value={historialSearch}
                  onChange={(e) => { setHistorialSearch(e.target.value); setHistorialPage(1); }}
                  placeholder="Paciente, medicamento, estado, médico, notas..."
                  disabled={!historialDni}
                  style={{ ...styles.input, maxWidth: 'none', width: '100%', height: 36, boxSizing: 'border-box', opacity: historialDni ? 1 : 0.6, cursor: historialDni ? 'text' : 'not-allowed' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Estado</label>
                <select value={historialEstado} onChange={(e) => { setHistorialEstado(e.target.value); setHistorialPage(1); }} disabled={!historialDni} style={{ ...styles.select, width: '100%', height: 36, boxSizing: 'border-box', cursor: historialDni ? 'pointer' : 'not-allowed', opacity: historialDni ? 1 : 0.6 }}>
                  {HISTORIAL_ESTADO_FILTER_OPTIONS.map((opt) => (
                    <option key={`hist-estado-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr 1fr' : '130px 130px 150px 90px', gap: 10, alignItems: 'end' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Desde</label>
                <input type="date" value={historialDesde} onChange={(e) => { setHistorialDesde(e.target.value); setHistorialPage(1); }} disabled={!historialDni} style={{ ...styles.select, cursor: historialDni ? 'text' : 'not-allowed', opacity: historialDni ? 1 : 0.6, width: '100%', height: 36, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Hasta</label>
                <input type="date" value={historialHasta} onChange={(e) => { setHistorialHasta(e.target.value); setHistorialPage(1); }} disabled={!historialDni} style={{ ...styles.select, cursor: historialDni ? 'text' : 'not-allowed', opacity: historialDni ? 1 : 0.6, width: '100%', height: 36, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Orden</label>
                <select value={historialOrder} onChange={(e) => { setHistorialOrder(e.target.value); setHistorialPage(1); }} disabled={!historialDni} style={{ ...styles.select, width: '100%', height: 36, boxSizing: 'border-box', cursor: historialDni ? 'pointer' : 'not-allowed', opacity: historialDni ? 1 : 0.6 }}>
                  <option value="fecha_desc">Mas reciente</option>
                  <option value="fecha_asc">Mas antiguo</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Mostrar</label>
                <select value={historialItemsPerPage} onChange={(e) => { setHistorialItemsPerPage(Number(e.target.value)); setHistorialPage(1); }} disabled={!historialDni} style={{ ...styles.select, width: '100%', height: 36, boxSizing: 'border-box', cursor: historialDni ? 'pointer' : 'not-allowed', opacity: historialDni ? 1 : 0.6 }}>
                  <option value={6}>6</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 'calc(100dvh - 310px)', overflow: 'auto', paddingRight: 2 }}>
              {historialLoading ? (
                <div style={{ ...styles.loadingBox, marginTop: 0 }}>
                  <div style={styles.spinner} aria-hidden="true" />
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#253342' }}>Cargando historial…</div>
                </div>
              ) : filteredHistorial.length === 0 ? (
                <div className="crm-icard-b" style={{ textAlign: 'center', color: 'var(--hs-text-xlight)', border: '1px solid rgba(148,163,184,.2)', borderRadius: 14, background: 'linear-gradient(180deg,#ffffff,#f8fafc)', padding: '28px 18px', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.75)' }}>
                  <div style={{ width: 56, height: 56, margin: '0 auto', borderRadius: '50%', background: 'linear-gradient(145deg,#eef2ff,#dbeafe)', border: '1px solid rgba(37,99,235,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#1d4ed8', boxShadow: '0 8px 20px rgba(37,99,235,.16)' }}>
                    🗂️
                  </div>
                  <div style={{ marginTop: 12, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Sin historial para mostrar</div>
                  <div style={{ marginTop: 6, fontSize: 13, color: '#64748b', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.45 }}>
                    Seleccioná otro paciente o ajustá el buscador para encontrar derivaciones ya enviadas al médico, aprobadas o rechazadas.
                  </div>
                </div>
              ) : (
                <>
                  {pageHistorial.map((p) => {
                    const draft = historialDrafts[p.id] || { medico_id: p.medico_id ? String(p.medico_id) : '', notas: p.notas || '' };
                    const saving = !!historialSaving[p.id];
                    const lockedByMedicalDecision = p.estado === 'aprobado' || p.estado === 'rechazado';
                    const isExpanded = Number(historialExpandedId) === Number(p.id);
                    return (
                      <div key={p.id} style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(148,163,184,.2)', background: 'linear-gradient(180deg,#ffffff,#f8fafc)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                          <div style={{ display: 'flex', gap: 10, minWidth: 0 }}>
                            <div style={{ ...styles.avatar, width: 38, height: 38 }}>{patientInitials(p.paciente)}</div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                {p.paciente}
                                <PacienteExternoBadge show={p.es_externo} compact />
                              </div>
                              <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>
                                DNI {formatDniDisplay(p.dni)}{p.solicitado ? ` · ${p.solicitado}` : ''}
                              </div>
                              <div style={{ marginTop: 4, fontSize: 12, color: '#33475B', fontWeight: 700 }}>
                                {p.medicamento_nombre || 'Cargando Medicamento...'}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <DerivacionEstadoPill estado={p.estado} />
                            <button
                              type="button"
                              onClick={() => setHistorialExpandedId((prev) => (Number(prev) === Number(p.id) ? null : p.id))}
                              aria-label={isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
                              style={{
                                border: '1px solid rgba(148,163,184,.25)',
                                background: '#fff',
                                cursor: 'pointer',
                                padding: 6,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 999,
                                transition: 'background .18s ease, transform .28s cubic-bezier(.2,.9,.2,1)',
                                boxShadow: '0 2px 8px rgba(15,23,42,.08)'
                              }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', color: '#334155', transform: isExpanded ? 'rotate(90deg) translateX(1px)' : 'rotate(0deg)', transition: 'transform .32s cubic-bezier(.2,.9,.2,1)' }} aria-hidden>
                                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" fill="currentColor" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div
                          style={{
                            overflow: 'hidden',
                            transition: 'max-height .34s cubic-bezier(.2,.9,.2,1), opacity .22s ease, margin-top .22s ease',
                            maxHeight: isExpanded ? 460 : 0,
                            opacity: isExpanded ? 1 : 0,
                            marginTop: isExpanded ? 10 : 0
                          }}
                        >
                          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 900 ? '1fr' : '1fr 1fr', gap: 12 }}>
                            <div>
                              <div style={styles.fieldLabel}>Médico actual</div>
                              <div style={styles.fieldValue}>
                                {p.medico ? formatDoctorDisplayName(p.medico) : (p.medico_id ? `Médico #${p.medico_id}` : 'Sin médico asignado')}
                              </div>
                              {(() => {
                                const internalMessages = parsePedidoInternalMessages(p.message_history);
                                const lastInternalMessage = internalMessages[internalMessages.length - 1] || null;
                                return (
                                  <button
                                    type="button"
                                    className="crm-gd-internal-chat-inline"
                                    onClick={() => openInternalChat(p.id)}
                                  >
                                    <span className="crm-gd-internal-chat-inline__icon" aria-hidden>◉</span>
                                    <span className="crm-gd-internal-chat-inline__copy">
                                      <strong>Canal interno con el médico</strong>
                                      <span>
                                        {lastInternalMessage
                                          ? `${lastInternalMessage.author}${lastInternalMessage.timestamp ? ` · ${lastInternalMessage.timestamp}` : ''} · ${lastInternalMessage.text}`
                                          : 'Sin mensajes · abrir chat flotante'}
                                      </span>
                                    </span>
                                    {internalMessages.length > 0 ? (
                                      <span className="crm-gd-internal-chat-inline__badge">
                                        {internalMessages.length}
                                      </span>
                                    ) : null}
                                  </button>
                                );
                              })()}
                            </div>
                            {lockedByMedicalDecision ? (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,.24)', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 800 }}>
                                  Edición bloqueada por decisión médica
                                </span>
                              </div>
                            ) : (
                              <div style={{ ...styles.derivBox, padding: 10 }}>
                                <div>
                                  <div style={styles.fieldLabel}>Corregir médico</div>
                                  <input
                                    type="search"
                                    value={historialMedicoBuscar[p.id] || ''}
                                    onChange={(e) => setHistorialMedicoBuscar((prev) => ({ ...prev, [p.id]: e.target.value }))}
                                    placeholder="Buscar por nombre, especialidad o matrícula…"
                                    disabled={saving}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Buscar médico para corrección"
                                    className="crm-gd-input"
                                    style={{
                                      width: '100%',
                                      maxWidth: 'none',
                                      minWidth: 0,
                                      flex: 'none',
                                      marginBottom: 6
                                    }}
                                  />
                                  {(() => {
                                    const qHist = String(historialMedicoBuscar[p.id] || '').trim().toLowerCase();
                                    const medicosHistorialLista = qHist
                                      ? medicos.filter((m) => {
                                        const n = String(m.nombre || '').toLowerCase();
                                        const e = String(m.especialidad || '').toLowerCase();
                                        const mat = String(m.matricula || '').toLowerCase();
                                        return n.includes(qHist) || e.includes(qHist) || mat.includes(qHist);
                                      })
                                      : medicos;
                                    return (
                                      <>
                                        {qHist ? (
                                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                                            Mostrando <strong style={{ color: '#334155' }}>{medicosHistorialLista.length}</strong> de {medicos.length} médicos
                                          </div>
                                        ) : null}
                                        {qHist && medicosHistorialLista.length === 0 ? (
                                          <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
                                            No hay médicos que coincidan con la búsqueda. Probá con otro texto o borrá el filtro.
                                          </div>
                                        ) : null}
                                        <div data-historial-medico-picker={p.id} style={{ position: 'relative' }}>
                                          <button
                                            type="button"
                                            disabled={saving}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (saving) return;
                                              setHistorialMedicoDropdownPedidoId((cur) => (String(cur) === String(p.id) ? null : p.id));
                                            }}
                                            aria-expanded={String(historialMedicoDropdownPedidoId) === String(p.id)}
                                            aria-haspopup="listbox"
                                            style={{
                                              width: '100%',
                                              minHeight: 40,
                                              padding: '6px 10px',
                                              borderRadius: 8,
                                              border: '1px solid #cbd5e1',
                                              fontSize: 13,
                                              background: saving ? '#f8fafc' : '#fff',
                                              cursor: saving ? 'not-allowed' : 'pointer',
                                              opacity: saving ? 0.7 : 1,
                                              boxSizing: 'border-box',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              gap: 10,
                                              textAlign: 'left'
                                            }}
                                          >
                                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#253342', fontWeight: 600 }}>
                                              {(() => {
                                                const sel = medicos.find((mm) => String(mm.id) === String(draft.medico_id || ''));
                                                if (!sel) return '— Seleccionar médico —';
                                                return `${formatDoctorDisplayName(sel.nombre)}${sel.especialidad ? ` — ${sel.especialidad}` : ''}`;
                                              })()}
                                            </span>
                                            <span style={{ flexShrink: 0, color: '#94a3b8', fontSize: 12, fontWeight: 800 }}>▾</span>
                                          </button>
                                          {String(historialMedicoDropdownPedidoId) === String(p.id) && !saving ? (
                                            <div
                                              role="listbox"
                                              style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                marginTop: 4,
                                                maxHeight: 280,
                                                overflowY: 'auto',
                                                zIndex: 2200,
                                                background: '#fff',
                                                border: '1px solid rgba(148,163,184,.32)',
                                                borderRadius: 10,
                                                boxShadow: '0 14px 36px rgba(15,23,42,.14), 0 2px 8px rgba(15,23,42,.06)'
                                              }}
                                            >
                                              <button
                                                type="button"
                                                role="option"
                                                aria-selected={!(draft.medico_id || '')}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setHistorialDrafts((prev) => ({
                                                    ...prev,
                                                    [p.id]: { ...(prev[p.id] || {}), medico_id: '' }
                                                  }));
                                                  setHistorialMedicoDropdownPedidoId(null);
                                                }}
                                                style={{
                                                  width: '100%',
                                                  padding: '10px 12px',
                                                  border: 'none',
                                                  borderBottom: '1px solid #f1f5f9',
                                                  background: !(draft.medico_id || '') ? '#eff6ff' : '#fff',
                                                  cursor: 'pointer',
                                                  fontSize: 13,
                                                  color: '#64748b',
                                                  fontWeight: 700,
                                                  textAlign: 'left',
                                                  boxSizing: 'border-box'
                                                }}
                                              >
                                                — Seleccionar médico —
                                              </button>
                                              {medicosHistorialLista.map((m) => {
                                                const selected = String(draft.medico_id || '') === String(m.id);
                                                return (
                                                  <button
                                                    key={`hist-m-${p.id}-${m.id}`}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={selected}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setHistorialDrafts((prev) => ({
                                                        ...prev,
                                                        [p.id]: { ...(prev[p.id] || {}), medico_id: String(m.id) }
                                                      }));
                                                      setHistorialMedicoDropdownPedidoId(null);
                                                    }}
                                                    style={{
                                                      width: '100%',
                                                      padding: '10px 12px',
                                                      border: 'none',
                                                      borderBottom: '1px solid #f1f5f9',
                                                      background: selected ? '#eff6ff' : '#fff',
                                                      cursor: 'pointer',
                                                      fontSize: 13,
                                                      color: '#253342',
                                                      textAlign: 'left',
                                                      boxSizing: 'border-box'
                                                    }}
                                                  >
                                                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                      {formatDoctorDisplayName(m.nombre)}
                                                      {m.especialidad ? ` — ${m.especialidad}` : ''}
                                                    </span>
                                                    {m.matricula ? (
                                                      <span style={{ display: 'block', marginTop: 2, fontSize: 11, color: '#64748b' }}>
                                                        Matrícula: {m.matricula}
                                                      </span>
                                                    ) : null}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          ) : null}
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                                <div>
                                  <div style={styles.fieldLabel}>Corregir nota</div>
                                  <textarea
                                    value={draft.notas || ''}
                                    onChange={(e) => setHistorialDrafts((prev) => ({
                                      ...prev,
                                      [p.id]: { ...(prev[p.id] || {}), notas: e.target.value }
                                    }))}
                                    placeholder="Nota administrativa para el médico…"
                                    style={{ ...styles.textarea, minHeight: 64, resize: 'none' }}
                                    disabled={saving}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => guardarHistorialPedido(p)}
                                  disabled={saving}
                                  style={{ ...styles.primaryBtn, ...(saving ? styles.primaryBtnDisabled : {}) }}
                                >
                                  {saving ? 'Guardando…' : 'Guardar corrección'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {filteredHistorial.length > 0 ? (
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  Mostrando {Math.min((historialPage - 1) * historialItemsPerPage + 1, filteredHistorial.length)}-{Math.min(historialPage * historialItemsPerPage, filteredHistorial.length)} de {filteredHistorial.length}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button disabled={historialPage <= 1} onClick={() => setHistorialPage((p) => Math.max(1, p - 1))} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,.3)', background: historialPage <= 1 ? '#f1f3f6' : '#fff', cursor: historialPage <= 1 ? 'not-allowed' : 'pointer', fontWeight: 700 }}>◀</button>
                  <div style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>Página {historialPage} / {totalHistorialPages}</div>
                  <button disabled={historialPage >= totalHistorialPages} onClick={() => setHistorialPage((p) => Math.min(totalHistorialPages, p + 1))} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,.3)', background: historialPage >= totalHistorialPages ? '#f1f3f6' : '#fff', cursor: historialPage >= totalHistorialPages ? 'not-allowed' : 'pointer', fontWeight: 700 }}>▶</button>
                </div>
              </div>
            ) : null}
          </div>
        </aside>
      </div>


      {!historialOpen && !medicoVistaOpen && !medicosStatsOpen && typeof document !== 'undefined' ? (() => {
        const popupMessages = internalChatPedido
          ? parsePedidoInternalMessages(internalChatPedido.message_history)
          : [];
        const isSendingInternalMessage = internalChatPedido
          ? Boolean(internalMessageSaving[internalChatPedido.id])
          : false;
        const showPicker = internalChatView === 'picker';
        const chatReady = Boolean(internalChatPedido);

        return createPortal(
          <div
            className={`crm-gd-internal-chat-shell${internalChatPanelOpen ? ' is-expanded' : ''}`}
            aria-live="polite"
          >
            {internalChatPanelOpen ? (
              <>
                <button
                  type="button"
                  className="crm-gd-internal-chat-shell__backdrop"
                  onClick={closeInternalChat}
                  aria-label="Cerrar chat interno"
                />
                <div
                  className={`crm-gd-internal-chat-popup${showPicker ? ' crm-gd-internal-chat-popup--picker' : ''}`}
                  role="dialog"
                  aria-modal="true"
                  aria-label={showPicker ? 'Elegir paciente para chat interno' : 'Chat interno con el médico'}
                >
              <header className="crm-gd-internal-chat-popup__head">
                <div className="crm-gd-internal-chat-popup__head-copy">
                  <div className="crm-gd-internal-chat-popup__steps" role="tablist" aria-label="Vistas del chat interno">
                    <button
                      type="button"
                      role="tab"
                      id="crm-gd-internal-chat-tab-chat"
                      className={`crm-gd-internal-chat-popup__step${!showPicker ? ' is-active' : ''}`}
                      aria-selected={!showPicker}
                      aria-controls="crm-gd-internal-chat-panel-chat"
                      onClick={handleInternalChatTabChat}
                    >
                      Chat
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="crm-gd-internal-chat-tab-picker"
                      className={`crm-gd-internal-chat-popup__step${showPicker ? ' is-active' : ''}`}
                      aria-selected={showPicker}
                      aria-controls="crm-gd-internal-chat-panel-picker"
                      onClick={handleInternalChatTabPaciente}
                    >
                      Paciente
                    </button>
                  </div>
                  <span className="crm-gd-internal-chat-popup__eyebrow">
                    <span aria-hidden>◉</span>
                    Canal interno
                  </span>
                  <h2 className="crm-gd-internal-chat-popup__title">
                    {showPicker ? 'Elegí un paciente' : 'Chat rápido con el médico'}
                  </h2>
                  {internalChatPedido ? (
                    <p className="crm-gd-internal-chat-popup__sub">
                      {internalChatPedido.paciente} · DNI {formatDniDisplay(internalChatPedido.dni)}
                      {internalChatPedido.medico ? ` · ${formatDoctorDisplayName(internalChatPedido.medico)}` : ''}
                    </p>
                  ) : showPicker ? (
                    <p className="crm-gd-internal-chat-popup__sub">
                      Seleccioná un paciente. Si tiene varias derivaciones, abrimos el chat más reciente.
                    </p>
                  ) : (
                    <p className="crm-gd-internal-chat-popup__sub">
                      Elegí un paciente en la pestaña Paciente para iniciar el chat con el médico.
                    </p>
                  )}
                </div>
                <div className="crm-gd-internal-chat-popup__head-actions">
                  <button
                    type="button"
                    className="crm-gd-internal-chat-popup__close"
                    onClick={closeInternalChat}
                    aria-label="Cerrar chat interno"
                  >
                    ×
                  </button>
                </div>
              </header>

              {showPicker ? (
                <div
                  id="crm-gd-internal-chat-panel-picker"
                  role="tabpanel"
                  aria-labelledby="crm-gd-internal-chat-tab-picker"
                  className="crm-gd-internal-chat-picker"
                >
                  {lastInternalChatPedido ? (
                    <button
                      type="button"
                      className="crm-gd-internal-chat-picker__last"
                      onClick={() => openInternalChat(lastInternalChatPedido.id, { openPanel: true })}
                    >
                      <span className="crm-gd-internal-chat-picker__last-icon" aria-hidden>↺</span>
                      <span className="crm-gd-internal-chat-picker__last-copy">
                        <strong>Continuar último chat</strong>
                        <span>
                          {lastInternalChatPedido.paciente}
                          {lastInternalChatSummary?.last ? ` · ${lastInternalChatSummary.last.text}` : ''}
                        </span>
                      </span>
                      {lastInternalChatSummary?.count > 0 ? (
                        <span className="crm-gd-internal-chat-picker__last-badge">
                          {lastInternalChatSummary.count}
                        </span>
                      ) : null}
                    </button>
                  ) : null}
                  <div className="crm-gd-internal-chat-picker__search-wrap">
                    <input
                      type="search"
                      className="crm-gd-input crm-gd-internal-chat-picker__search"
                      value={internalChatPickerSearch}
                      onChange={(e) => setInternalChatPickerSearch(e.target.value)}
                      placeholder="Buscar paciente, DNI o medicamento…"
                      aria-label="Buscar paciente para chat interno"
                    />
                  </div>
                  <ul className="crm-gd-internal-chat-picker__list">
                    {filteredInternalChatPatientGroups.length === 0 ? (
                      <li className="crm-gd-internal-chat-picker__empty">
                        No hay pacientes para mostrar.
                      </li>
                    ) : (
                      filteredInternalChatPatientGroups.map((group) => {
                        const isSelectedPatient = Number(internalChatPedidoId) === Number(group.primaryPedidoId);
                        return (
                        <li key={`internal-chat-pick-${group.key}`}>
                          <button
                            type="button"
                            className={`crm-gd-internal-chat-picker__item${isSelectedPatient ? ' is-selected' : ''}`}
                            onClick={() => openInternalChat(group.primaryPedidoId, { openPanel: true })}
                          >
                            <span className="crm-gd-internal-chat-picker__avatar" aria-hidden>
                              {patientInitials(group.paciente)}
                            </span>
                            <span className="crm-gd-internal-chat-picker__item-copy">
                              <strong>{group.paciente}</strong>
                              <span>{formatInternalChatGroupSubtitle(group)}</span>
                            </span>
                            {group.totalMessageCount > 0 ? (
                              <span className="crm-gd-internal-chat-picker__item-badge">
                                {group.totalMessageCount}
                              </span>
                            ) : null}
                          </button>
                        </li>
                        );
                      })
                    )}
                  </ul>
                </div>
              ) : (
                <div
                  id="crm-gd-internal-chat-panel-chat"
                  role="tabpanel"
                  aria-labelledby="crm-gd-internal-chat-tab-chat"
                  className="crm-gd-internal-chat-conversation"
                >
                  <div className="crm-gd-internal-chat-popup__messages">
                    {!chatReady ? (
                      <div className="crm-gd-internal-chat-popup__empty">
                        <strong>Todavía no elegiste paciente</strong>
                        <span>Andá a la pestaña Paciente para elegir con quién chatear con el médico.</span>
                        <button
                          type="button"
                          className="crm-gd-internal-chat-popup__pick-btn"
                          onClick={handleInternalChatTabPaciente}
                        >
                          Elegir paciente
                        </button>
                      </div>
                    ) : popupMessages.length > 0 ? (
                      <div className="crm-gd-internal-chat-popup__thread">
                        {popupMessages.map((msg) => {
                          const isAdministrative = msg.tone === 'administracion';
                          const isLegacy = msg.tone === 'legacy';
                          return (
                            <div
                              key={msg.id}
                              className={`crm-gd-internal-chat-bubble${isAdministrative ? ' crm-gd-internal-chat-bubble--admin' : ''}${isLegacy ? ' crm-gd-internal-chat-bubble--legacy' : ''}`}
                            >
                              <div className="crm-gd-internal-chat-bubble__meta">
                                <strong>{msg.author}</strong>
                                {msg.timestamp ? <span>{msg.timestamp}</span> : null}
                              </div>
                              <div className="crm-gd-internal-chat-bubble__text">{msg.text}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="crm-gd-internal-chat-popup__empty">
                        <strong>Sin mensajes por ahora</strong>
                        <span>Enviá una indicación breve al médico sin salir de Gestión Derivación.</span>
                      </div>
                    )}
                  </div>

                  {chatReady ? (
                    <footer className="crm-gd-internal-chat-popup__composer">
                      <textarea
                        value={internalMessageDrafts[internalChatPedido.id] || ''}
                        onChange={(e) => setInternalMessageDrafts((prev) => ({ ...prev, [internalChatPedido.id]: e.target.value }))}
                        placeholder="Escribí tu mensaje interno…"
                        className="crm-gd-textarea crm-gd-internal-chat-popup__textarea"
                        disabled={isSendingInternalMessage}
                      />
                      <button
                        type="button"
                        className="crm-gd-btn crm-gd-btn--primary crm-gd-internal-chat-popup__send"
                        onClick={() => enviarMensajeInternoHistorial(internalChatPedido)}
                        disabled={isSendingInternalMessage}
                      >
                        {isSendingInternalMessage ? 'Enviando…' : 'Enviar'}
                      </button>
                    </footer>
                  ) : null}
                </div>
              )}
                </div>
              </>
            ) : (
              <div className="crm-gd-internal-chat-shell__rail">
                <button
                  type="button"
                  className="crm-gd-internal-chat-shell__btn"
                  onClick={openInternalChatLauncher}
                  title={lastInternalChatPedido ? 'Abrir último chat con el médico' : 'Canal interno con el médico'}
                  aria-label={lastInternalChatPedido ? 'Abrir último chat con el médico' : 'Abrir chat interno con el médico'}
                >
                  <span className="crm-gd-internal-chat-shell__btn-icon" aria-hidden>◉</span>
                  <span className="crm-gd-internal-chat-shell__btn-label" aria-hidden>Chat</span>
                  {lastInternalChatSummary?.count > 0 ? (
                    <span className="crm-gd-internal-chat-shell__badge">{lastInternalChatSummary.count}</span>
                  ) : null}
                </button>
              </div>
            )}
          </div>,
          document.body
        );
      })() : null}

      <div
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--left${medicoVistaOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'}`}
        onClick={(e) => { if (e.target === e.currentTarget) closeMedicoVista(); }}
      >
        <aside
          onClick={(e) => e.stopPropagation()}
          className={`crm-gd-drawer-panel${medicoVistaOpen ? ' crm-gd-drawer-panel--left-open' : ' crm-gd-drawer-panel--left-closed'}`}
          style={{
            width: window.innerWidth <= 768 ? '100vw' : 'min(480px, 42vw)',
            minWidth: window.innerWidth <= 768 ? 0 : 380,
            maxWidth: '100vw',
            padding: window.innerWidth <= 768 ? 12 : 18,
            borderRight: window.innerWidth <= 768 ? 'none' : undefined,
            boxShadow: '24px 0 50px rgba(15, 23, 42, 0.2), 8px 0 20px rgba(15, 23, 42, 0.1)'
          }}
        >
          <div className="crm-gd-drawer-head">
            <div style={{ minWidth: 0 }}>
              <div className="crm-gd-drawer-head__title">Pedidos por médico</div>
              <div className="crm-gd-drawer-head__sub">
                Filtrá por profesional y revisá solo las derivaciones ya asignadas a esa persona (pendientes de aprobación, aprobadas o rechazadas).
              </div>
            </div>
            <button type="button" className="crm-gd-btn" onClick={closeMedicoVista}>
              Cerrar
            </button>
          </div>

          <div style={{ marginTop: 14, flexShrink: 0, display: 'grid', gridTemplateColumns: window.innerWidth <= 900 ? '1fr' : '1fr 260px', gap: 10, alignItems: 'start' }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block', fontWeight: 700 }}>Médico</label>
              <input
                type="search"
                value={medicoVistaMedicoFiltro}
                onChange={(e) => setMedicoVistaMedicoFiltro(e.target.value)}
                placeholder="Buscar por nombre, especialidad o matrícula…"
                disabled={medicosLoading}
                aria-label="Buscar médico en la lista"
                style={{
                  ...styles.input,
                  width: '100%',
                  maxWidth: 'none',
                  minWidth: 0,
                  flex: 'none',
                  height: 38,
                  boxSizing: 'border-box',
                  marginBottom: 0,
                  opacity: medicosLoading ? 0.65 : 1,
                  cursor: medicosLoading ? 'wait' : 'text'
                }}
              />
              {String(medicoVistaMedicoFiltro || '').trim() ? (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                  Mostrando <strong style={{ color: '#334155' }}>{medicosParaSelectorMedicoVista.length}</strong>
                  {' '}de {medicos.length} médicos
                </div>
              ) : null}
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block', fontWeight: 700 }}>Ordenar médicos</label>
              <select
                value={medicoVistaMedicoOrder}
                onChange={(e) => setMedicoVistaMedicoOrder(e.target.value)}
                style={{ ...styles.select, width: '100%', height: 38, boxSizing: 'border-box', cursor: 'pointer' }}
              >
                <option value="badge_desc">Tratamientos: mayor a menor</option>
                <option value="badge_asc">Tratamientos: menor a mayor</option>
                <option value="nombre_asc">Nombre: A a Z</option>
                <option value="nombre_desc">Nombre: Z a A</option>
              </select>
            </div>
            <div ref={medicoVistaMedicoPickerRef} style={{ position: 'relative', gridColumn: '1 / -1' }}>
              <button
                type="button"
                disabled={medicosLoading}
                onClick={() => { if (!medicosLoading) setMedicoVistaMedicoListOpen((o) => !o); }}
                aria-expanded={medicoVistaMedicoListOpen}
                aria-haspopup="listbox"
                style={{
                  width: '100%',
                  minHeight: 40,
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: 13,
                  background: medicosLoading ? '#f8fafc' : '#fff',
                  cursor: medicosLoading ? 'wait' : 'pointer',
                  opacity: medicosLoading ? 0.75 : 1,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  textAlign: 'left'
                }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#253342', fontWeight: 600 }}>
                  {medicosLoading
                    ? 'Cargando médicos…'
                    : (() => {
                      const sel = medicos.find((mm) => String(mm.id) === String(medicoVistaMedicoId));
                      if (!sel) return '— Elegí un médico —';
                      return `${formatDoctorDisplayName(sel.nombre)}${sel.especialidad ? ` — ${sel.especialidad}` : ''}`;
                    })()}
                </span>
                {medicoVistaMedicoId && !medicosLoading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <TratamientosCountBadge count={tratamientosCountByMedicoId.get(String(medicoVistaMedicoId)) ?? 0} />
                    <span style={{ color: '#94a3b8', fontSize: 10, fontWeight: 800, lineHeight: 1 }}>▾</span>
                  </span>
                ) : (
                  <span style={{ flexShrink: 0, color: '#94a3b8', fontSize: 12, fontWeight: 800 }}>▾</span>
                )}
              </button>

              {medicoVistaMedicoListOpen && !medicosLoading ? (
                <div
                  role="listbox"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: 4,
                    maxHeight: 280,
                    overflowY: 'auto',
                    zIndex: 50,
                    background: '#fff',
                    border: '1px solid rgba(148,163,184,.32)',
                    borderRadius: 10,
                    boxShadow: '0 14px 36px rgba(15,23,42,.14), 0 2px 8px rgba(15,23,42,.06)'
                  }}
                >
                  <button
                    type="button"
                    role="option"
                    aria-selected={!medicoVistaMedicoId}
                    onClick={() => { setMedicoVistaMedicoId(''); setMedicoVistaBuscar(''); setMedicoVistaMedicoListOpen(false); }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      background: !medicoVistaMedicoId ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#64748b',
                      fontWeight: 700,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      boxSizing: 'border-box'
                    }}
                  >
                    <span>— Elegí un médico —</span>
                  </button>
                  {medicosParaSelectorMedicoVista.map((m) => {
                    const nTrat = tratamientosCountByMedicoId.get(String(m.id)) ?? 0;
                    const selected = String(medicoVistaMedicoId) === String(m.id);
                    return (
                      <button
                        key={`mv-row-${m.id}`}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          setMedicoVistaMedicoId(String(m.id));
                          setMedicoVistaBuscar('');
                          setMedicoVistaMedicoListOpen(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: 'none',
                          borderBottom: '1px solid #f1f5f9',
                          background: selected ? '#eff6ff' : '#fff',
                          cursor: 'pointer',
                          fontSize: 13,
                          color: '#253342',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          boxSizing: 'border-box'
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          {formatDoctorDisplayName(m.nombre)}
                          {m.especialidad ? ` — ${m.especialidad}` : ''}
                        </span>
                        <TratamientosCountBadge count={nTrat} />
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
            {String(medicoVistaMedicoFiltro || '').trim() && medicosParaSelectorMedicoVista.length === 0 ? (
              <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>
                No hay médicos que coincidan. Probá otro texto o borrá el filtro.
              </div>
            ) : null}
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, lineHeight: 1.35, gridColumn: '1 / -1' }}>
              El círculo a la derecha muestra cuántos tratamientos derivados tiene en el historial; el color depende del número (mismo total, mismo tono).
            </div>
          </div>

          <div style={{ marginTop: 10, flexShrink: 0 }}>
            <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block', fontWeight: 700 }}>Buscar en esta lista</label>
            <input
              value={medicoVistaBuscar}
              onChange={(e) => setMedicoVistaBuscar(e.target.value)}
              placeholder="Paciente, DNI, medicamento, estado, notas…"
              disabled={!medicoVistaMedicoId}
              style={{ ...styles.input, width: '100%', maxWidth: 'none', height: 38, boxSizing: 'border-box', opacity: medicoVistaMedicoId ? 1 : 0.55, cursor: medicoVistaMedicoId ? 'text' : 'not-allowed' }}
            />
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block', fontWeight: 700 }}>Ordenar lista</label>
              <select
                value={medicoVistaListaOrder}
                onChange={(e) => setMedicoVistaListaOrder(e.target.value)}
                disabled={!medicoVistaMedicoId}
                style={{ ...styles.select, width: '100%', height: 38, boxSizing: 'border-box', cursor: medicoVistaMedicoId ? 'pointer' : 'not-allowed', opacity: medicoVistaMedicoId ? 1 : 0.55 }}
              >
                <option value="fecha_desc">Fecha: más reciente</option>
                <option value="fecha_asc">Fecha: más antigua</option>
                <option value="paciente_asc">Paciente: A a Z</option>
                <option value="paciente_desc">Paciente: Z a A</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: '#475569', fontWeight: 700, flexShrink: 0 }}>
            {medicoVistaMedicoId
              ? `${pedidosPorMedicoSeleccion.length} pedido${pedidosPorMedicoSeleccion.length === 1 ? '' : 's'} asignado${pedidosPorMedicoSeleccion.length === 1 ? '' : 's'}`
              : 'Seleccioná un médico para ver su bandeja'}
          </div>

          <div style={{ marginTop: 10, flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 2 }}>
            {historialLoading ? (
              <div style={{ ...styles.loadingBox, marginTop: 0, minHeight: 140 }}>
                <div style={styles.spinner} aria-hidden="true" />
                <div style={{ fontSize: 14, fontWeight: 800, color: '#253342' }}>Actualizando datos…</div>
                <div style={{ fontSize: 12, color: '#7C98B6' }}>Sincronizando historial de derivaciones</div>
              </div>
            ) : !medicoVistaMedicoId ? (
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13, padding: '22px 12px', border: '1px dashed rgba(148,163,184,.4)', borderRadius: 12, background: '#fff' }}>
                Elegí un médico en el selector para concentrar la vista y reducir ruido en pantallas con mucho movimiento.
              </div>
            ) : pedidosPorMedicoSeleccion.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13, padding: '22px 12px', border: '1px dashed rgba(148,163,184,.4)', borderRadius: 12, background: '#fff' }}>
                No hay pedidos derivados a {formatDoctorDisplayName(medicos.find((m) => String(m.id) === String(medicoVistaMedicoId))?.nombre || 'este médico')} en el historial cargado.
              </div>
            ) : (
              pedidosPorMedicoSeleccion.map((p) => {
                return (
                  <div key={`medico-vista-${p.id}`} style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(148,163,184,.2)', background: 'linear-gradient(180deg,#ffffff,#f8fafc)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: 10, minWidth: 0 }}>
                        <div style={{ ...styles.avatar, width: 36, height: 36, fontSize: 11 }}>{patientInitials(p.paciente)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {p.paciente}
                            <PacienteExternoBadge show={p.es_externo} compact />
                          </div>
                          <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>
                            DNI {formatDniDisplay(p.dni)}{p.solicitado ? ` · ${p.solicitado}` : ''}
                          </div>
                          <div style={{ marginTop: 6, fontSize: 12, color: '#33475B', fontWeight: 700 }}>
                            {p.medicamento_nombre || '—'}
                          </div>
                          {(p.medico || p.notas) ? (
                            <div style={{ marginTop: 6, fontSize: 11, color: '#64748b', lineHeight: 1.35 }}>
                              {p.medico ? <div><strong>Médico en pedido:</strong> {formatDoctorDisplayName(p.medico)}</div> : null}
                              {p.notas ? <div style={{ marginTop: 4 }}><strong>Nota:</strong> {p.notas}</div> : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <DerivacionEstadoPill estado={p.estado} className="crm-gd-estado-pill--compact" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>
      </div>


      <div
        onClick={(e) => { if (e.target === e.currentTarget) setMedicosStatsOpen(false); }}
        style={{
          position: 'fixed',
          inset: 0,
          background: medicosStatsOpen ? 'rgba(15,23,42,.45)' : 'rgba(15,23,42,0)',
          zIndex: 2530,
          pointerEvents: medicosStatsOpen ? 'all' : 'none',
          transition: 'background .25s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: window.innerWidth <= 768 ? 10 : 22
        }}
      >
        <aside
          onClick={(e) => e.stopPropagation()}
          style={{
            width: statsIsMobile ? '100vw' : 'min(980px, calc(100vw - 220px))',
            maxHeight: statsIsMobile ? '88dvh' : '92dvh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: statsIsMobile ? '16px 16px 0 0' : 16,
            border: '1px solid rgba(148,163,184,.25)',
            background: 'linear-gradient(180deg,#ffffff,#f8fbff)',
            boxShadow: '0 34px 80px rgba(15,23,42,.3), 0 12px 26px rgba(15,23,42,.14)',
            transform: medicosStatsOpen ? 'translateY(0) scale(1)' : 'translateY(12px) scale(.985)',
            opacity: medicosStatsOpen ? 1 : 0,
            transition: 'transform .28s cubic-bezier(.2,.9,.2,1), opacity .2s ease'
          }}
        >
          <div
            style={{
              flexShrink: 0,
              padding: statsIsMobile ? 12 : 18,
              paddingBottom: 14,
              background: 'linear-gradient(180deg,rgba(248,251,255,.98),rgba(255,255,255,.94))',
              borderBottom: '1px solid rgba(226,232,240,.9)',
              boxShadow: '0 10px 26px rgba(15,23,42,.08)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a' }}>Panel de distribución por médico</div>
                <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>
                  Conteo acumulado basado en los mismos badges del selector (tratamientos derivados en historial).
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMedicosStatsOpen(false)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: '1px solid rgba(148,163,184,.35)',
                  background: 'linear-gradient(180deg,#ffffff,#f8fafc)',
                  cursor: 'pointer',
                  fontWeight: 700,
                  color: '#0f172a',
                  boxShadow: '0 6px 18px rgba(148,163,184,.14)'
                }}
              >
                Cerrar
              </button>
            </div>

            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: statsIsMobile ? '1fr 1fr' : (statsIsCompact ? 'repeat(2,minmax(0,1fr))' : 'repeat(4,minmax(0,1fr))'), gap: 10 }}>
              <div className="crm-icard"><div className="crm-icard-b" style={{ textAlign: 'center', borderRadius: 14, background: 'linear-gradient(180deg,#ffffff,#f8fbff)' }}><div style={styles.kpiNum}>{medicosConTratamientos}</div><div style={styles.kpiLabel}>Médicos con tratamientos</div></div></div>
              <div className="crm-icard"><div className="crm-icard-b" style={{ textAlign: 'center', borderRadius: 14, background: 'linear-gradient(180deg,#ffffff,#f8fbff)' }}><div style={styles.kpiNum}>{medicosSinTratamientos}</div><div style={styles.kpiLabel}>Médicos sin tratamientos</div></div></div>
              <div className="crm-icard"><div className="crm-icard-b" style={{ textAlign: 'center', borderRadius: 14, background: 'linear-gradient(180deg,#ffffff,#f8fbff)' }}><div style={styles.kpiNum}>{maxTratamientosMedico}</div><div style={styles.kpiLabel}>Máximo por medico</div></div></div>
              <div className="crm-icard"><div className="crm-icard-b" style={{ textAlign: 'center', borderRadius: 14, background: 'linear-gradient(180deg,#ffffff,#f8fbff)' }}><div style={styles.kpiNum}>{totalTratamientosAcumulados}</div><div style={styles.kpiLabel}>Acumulado total</div></div></div>
            </div>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: statsIsMobile ? 12 : 18, background: 'linear-gradient(180deg,rgba(248,250,252,.82),rgba(255,255,255,.96))' }}>
          <div style={{ border: '1px solid #DFE3EB', borderRadius: 16, background: 'rgba(255,255,255,.96)', padding: 12, boxShadow: '0 18px 42px rgba(15,23,42,.08)' }}>
            {historialLoading ? (
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13, padding: '22px 12px' }}>
                Cargando historial para construir el gráfico…
              </div>
            ) : medicosStatsRows.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13, padding: '22px 12px' }}>
                Sin datos: aún no hay médicos con tratamientos derivados en el historial cargado.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: statsIsCompact ? '1fr' : '270px 1fr', gap: 16 }}>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, background: 'linear-gradient(180deg,#f8fbff,#ffffff)' }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em' }}>Distribución médicos</div>
                  <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
                    <svg width="164" height="164" viewBox="0 0 164 164" aria-label="Donut de médicos activos">
                      <defs>
                        <linearGradient id="medStatsActive" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#1d4ed8" />
                          <stop offset="100%" stopColor="#06b6d4" />
                        </linearGradient>
                      </defs>
                      <g transform="translate(82,82) rotate(-90)">
                        <circle cx="0" cy="0" r={donutRadius} fill="none" stroke="#e2e8f0" strokeWidth="18" />
                        <circle
                          cx="0"
                          cy="0"
                          r={donutRadius}
                          fill="none"
                          stroke="url(#medStatsActive)"
                          strokeWidth="18"
                          strokeLinecap="round"
                          strokeDasharray={`${donutActiveLen} ${Math.max(0, donutCircumference - donutActiveLen)}`}
                        />
                      </g>
                      <text x="82" y="76" textAnchor="middle" style={{ fontSize: 26, fontWeight: 900, fill: '#0f172a' }}>{medicosActivosPct}%</text>
                      <text x="82" y="96" textAnchor="middle" style={{ fontSize: 10, fontWeight: 700, fill: '#64748b', letterSpacing: '.04em' }}>ACTIVOS</text>
                    </svg>
                  </div>
                  <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#334155', fontWeight: 700 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: '#1d4ed8', display: 'inline-block' }} />
                        Con tratamientos
                      </span>
                      <strong>{medicosConTratamientos}</strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#334155', fontWeight: 700 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: '#94a3b8', display: 'inline-block' }} />
                        Sin tratamientos
                      </span>
                      <strong>{medicosSinTratamientos}</strong>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(226,232,240,.9)' }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em' }}>Salud operativa</div>
                    <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                      <div style={{ borderRadius: 12, padding: '10px 11px', background: 'linear-gradient(180deg,#eff6ff,#ffffff)', border: '1px solid rgba(147,197,253,.35)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.85)' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{promedioTratamientosActivoLabel}</div>
                        <div style={{ marginTop: 4, fontSize: 10, color: '#475569', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }}>Promedio activo</div>
                        <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>Tratamientos por medico con carga</div>
                      </div>
                      <div style={{ borderRadius: 12, padding: '10px 11px', background: 'linear-gradient(180deg,#ecfeff,#ffffff)', border: '1px solid rgba(103,232,249,.42)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.85)' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{topCincoCargaPct}%</div>
                        <div style={{ marginTop: 4, fontSize: 10, color: '#475569', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }}>Top 5 concentran</div>
                        <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>{topCincoTratamientos} tratamientos del total</div>
                      </div>
                      <div style={{ borderRadius: 12, padding: '10px 11px', background: 'linear-gradient(180deg,#f8fafc,#ffffff)', border: '1px solid rgba(148,163,184,.25)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.85)' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{medianaTratamientosMedicoLabel}</div>
                        <div style={{ marginTop: 4, fontSize: 10, color: '#475569', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }}>Carga mediana</div>
                        <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>Punto medio entre médicos activos</div>
                      </div>
                      <div style={{ borderRadius: 12, padding: '10px 11px', background: 'linear-gradient(180deg,#fff7ed,#ffffff)', border: '1px solid rgba(253,186,116,.38)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.85)' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{medicosConUnaCarga}</div>
                        <div style={{ marginTop: 4, fontSize: 10, color: '#475569', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }}>Con carga minima</div>
                        <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>Médicos con un solo tratamiento</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, background: '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 320px', minWidth: 0, flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        value={medicosStatsSearch}
                        onChange={(e) => { setMedicosStatsSearch(e.target.value); setMedicosStatsPage(1); }}
                        placeholder="Buscar por medico, especialidad o matrícula..."
                        style={{ ...styles.input, minWidth: statsIsCompact ? '100%' : 280, maxWidth: 'none', width: statsIsCompact ? '100%' : 340, height: 38, boxSizing: 'border-box', borderRadius: 999, background: 'linear-gradient(180deg,#ffffff,#f8fafc)' }}
                      />
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 999, border: '1px solid rgba(148,163,184,.25)', background: 'linear-gradient(180deg,#f8fbff,#ffffff)', color: '#334155', fontSize: 12, fontWeight: 700 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: '#0ea5e9', display: 'inline-block', boxShadow: '0 0 0 4px rgba(14,165,233,.12)' }} />
                        {medicosStatsFiltered.length} médicos en vista
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                      Mostrando {Math.min((medicosStatsPage - 1) * medicosStatsItemsPerPage + 1, medicosStatsFiltered.length)}-{Math.min(medicosStatsPage * medicosStatsItemsPerPage, medicosStatsFiltered.length)} de {medicosStatsFiltered.length}
                    </div>
                  </div>
                  {!statsIsCompact ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr 142px', gap: 8, padding: '0 0 8px', borderBottom: '1px solid #f1f5f9', marginBottom: 8, fontSize: 11, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      <span>Médico</span><span>Carga relativa</span><span style={{ textAlign: 'right' }}>Cantidad</span>
                    </div>
                  ) : null}
                  <div style={{ display: 'grid', gap: 8 }}>
                    {medicosStatsFiltered.length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13, padding: '28px 12px', border: '1px dashed rgba(148,163,184,.35)', borderRadius: 12, background: 'linear-gradient(180deg,#ffffff,#f8fafc)' }}>
                        No hay médicos que coincidan con la búsqueda actual.
                      </div>
                    ) : medicosStatsTop.map((row, idx) => {
                      const percent = maxTratamientosMedico > 0 ? Math.round((row.total / maxTratamientosMedico) * 100) : 0;
                      const safePercent = Math.max(10, percent);
                      const hue = (row.total * 47) % 360;
                      const ranking = ((medicosStatsPage - 1) * medicosStatsItemsPerPage) + idx + 1;
                      return (
                        <div key={`med-st-${row.id}`} style={{ display: 'grid', gridTemplateColumns: statsIsCompact ? '1fr' : '300px 1fr 142px', alignItems: 'center', gap: 8, padding: statsIsCompact ? '8px' : 0, border: statsIsCompact ? '1px solid #eef2f7' : 'none', borderRadius: statsIsCompact ? 10 : 0 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {ranking}. {formatDoctorDisplayName(row.nombre)}
                            </div>
                            {row.especialidad ? (
                              <div style={{ marginTop: 2, fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {row.especialidad}{row.matricula ? ` · MP ${row.matricula}` : ''}
                              </div>
                            ) : null}
                            {!row.especialidad && row.matricula ? (
                              <div style={{ marginTop: 2, fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                MP {row.matricula}
                              </div>
                            ) : null}
                          </div>
                          <div style={{ width: '100%', height: 18, borderRadius: 999, background: '#f1f5f9', border: '1px solid #e2e8f0', overflow: 'hidden', position: 'relative' }}>
                            <div
                              style={{
                                height: '100%',
                                width: `${safePercent}%`,
                                borderRadius: 999,
                                background: `linear-gradient(90deg,hsl(${hue} 70% 56%),hsl(${(hue + 28) % 360} 76% 50%))`,
                                transition: 'width .35s ease'
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: statsIsCompact ? 'space-between' : 'flex-end', gap: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 900, color: '#1e293b', minWidth: 20, textAlign: 'right' }}>{row.total}</div>
                            <button
                              type="button"
                              onClick={() => openMedicoVistaFromStats(row.id)}
                              title={`Ver ${formatDoctorDisplayName(row.nombre)} en pedidos por medico`}
                              style={{
                                height: 28,
                                padding: '0 10px',
                                borderRadius: 999,
                                border: '1px solid rgba(37,99,235,.35)',
                                background: 'linear-gradient(180deg,#ffffff,#f8fbff)',
                                color: '#1d4ed8',
                                fontWeight: 800,
                                fontSize: 11,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Abrir bandeja
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {medicosStatsFiltered.length > 0 ? (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 999, background: 'rgba(241,245,249,.9)', color: '#475569', fontSize: 12, fontWeight: 700 }}>
                        Página {medicosStatsPage} de {totalMedicosStatsPages}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                          type="button"
                          disabled={medicosStatsPage <= 1}
                          onClick={() => setMedicosStatsPage((page) => Math.max(1, page - 1))}
                          style={{ padding: '7px 12px', borderRadius: 999, border: '1px solid rgba(148,163,184,.3)', background: medicosStatsPage <= 1 ? '#f1f3f6' : '#fff', color: medicosStatsPage <= 1 ? '#94a3b8' : '#0f172a', cursor: medicosStatsPage <= 1 ? 'not-allowed' : 'pointer', fontWeight: 800 }}
                        >
                          ◀ Anterior
                        </button>
                        <button
                          type="button"
                          disabled={medicosStatsPage >= totalMedicosStatsPages}
                          onClick={() => setMedicosStatsPage((page) => Math.min(totalMedicosStatsPages, page + 1))}
                          style={{ padding: '7px 12px', borderRadius: 999, border: '1px solid rgba(37,99,235,.35)', background: medicosStatsPage >= totalMedicosStatsPages ? '#f1f3f6' : 'linear-gradient(180deg,#ffffff,#eff6ff)', color: medicosStatsPage >= totalMedicosStatsPages ? '#94a3b8' : '#1d4ed8', cursor: medicosStatsPage >= totalMedicosStatsPages ? 'not-allowed' : 'pointer', fontWeight: 800 }}
                        >
                          Siguiente ▶
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
          </div>
        </aside>
      </div>

      {medicationDrawerPedido ? (() => {
        const medicationDisplay = buildMedicationDisplay(medicationDrawerPedido);
        const medicationDetails = normalizeMedicationFields(medicationDrawerPedido);
        const isMedicationDrawerMobile = window.innerWidth <= 900;
        const infoRows = [
          { label: 'Nombre comercial', value: medicationDisplay.title },
          { label: 'Laboratorio', value: medicationDetails.laboratorio },
          { label: 'Principio activo', value: capitalizeFirstLetter(medicationDetails.nombre_generico) },
          { label: 'Presentación', value: medicationDetails.presentacion },
          { label: 'Concentración', value: medicationDetails.concentracion },
          { label: 'Acción terapéutica', value: capitalizeFirstLetter(medicationDetails.accion_nombre) },
          { label: 'Categoría', value: capitalizeFirstLetter(medicationDetails.categoria_nombre) },
          { label: 'Troquel', value: medicationDetails.troquel },
          { label: 'Condición de venta', value: medicationDetails.condicion_venta },
          { label: 'Refrigeración', value: String(medicationDetails.refrigeracion || '').trim().toUpperCase() === 'S' ? 'Requiere cadena de frío' : '' },
          { label: 'Tratamiento crónico', value: medicationDetails.es_cronico ? 'Sí' : '' }
        ].filter((item) => String(item.value || '').trim());
        const infoColumns = isMedicationDrawerMobile
          ? [infoRows]
          : [
            infoRows.filter((_, index) => index % 2 === 0),
            infoRows.filter((_, index) => index % 2 === 1)
          ];

        return (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) closeMedicationDrawer(); }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'radial-gradient(circle at top right, rgba(96,165,250,.18), transparent 28%), rgba(15,23,42,.36)',
              backdropFilter: 'blur(8px)',
              /* Por encima del nav dock inferior (z-index 1200 en crm-sidebar-premium). */
              zIndex: 1280,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: isMedicationDrawerMobile ? 'stretch' : 'center',
              padding: isMedicationDrawerMobile ? 0 : 16
            }}
          >
            <aside
              style={{
                width: isMedicationDrawerMobile ? '100vw' : 'min(90vw, 420px)',
                maxHeight: isMedicationDrawerMobile ? '100dvh' : 'min(92dvh, 760px)',
                height: isMedicationDrawerMobile ? '100dvh' : 'auto',
                background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 62%,#f3f8ff 100%)',
                border: '1px solid rgba(219,229,242,.92)',
                borderRadius: isMedicationDrawerMobile ? 0 : 22,
                boxShadow: isMedicationDrawerMobile
                  ? '-20px 0 46px rgba(2,8,20,.22)'
                  : '0 34px 90px rgba(8,18,40,.26), 0 12px 28px rgba(8,18,40,.12)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <div style={{ position: 'relative', padding: isMedicationDrawerMobile ? '14px 14px 12px' : '14px 14px 12px', borderBottom: '1px solid rgba(232,238,247,.96)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, overflow: 'hidden' }}>
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: -80,
                    right: -50,
                    width: 220,
                    height: 220,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(59,130,246,.18) 0%, rgba(96,165,250,.08) 42%, transparent 72%)',
                    pointerEvents: 'none'
                  }}
                />
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: 'linear-gradient(180deg,#3b82f6,#1d4ed8)', boxShadow: '0 0 0 4px rgba(59,130,246,.12)' }} />
                    <div style={{ fontSize: 10, color: '#2563eb', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.14em' }}>Detalle principal</div>
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 900, color: '#172554', letterSpacing: '-.01em' }}>Ficha de medicamento</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.3 }}>
                    Validación de identidad y requisitos operativos.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeMedicationDrawer}
                  aria-label="Cerrar ficha de medicamento"
                  style={{ width: 30, height: 30, borderRadius: 999, border: '1px solid rgba(211,222,236,.92)', background: 'linear-gradient(180deg,#ffffff,#f8fbff)', cursor: 'pointer', flexShrink: 0, color: '#475569', boxShadow: '0 8px 18px rgba(15,23,42,.08)', position: 'relative' }}
                >
                  ✕
                </button>
              </div>

              <div style={{ padding: isMedicationDrawerMobile ? 12 : 14, overflowY: isMedicationDrawerMobile ? 'auto' : 'hidden', display: 'grid', gap: 10 }}>
                <div style={{ border: '1px solid rgba(37,99,235,.16)', borderRadius: 18, background: 'linear-gradient(180deg,#ffffff,#f5f9ff)', padding: 12, boxShadow: '0 18px 36px rgba(37,99,235,.08), inset 0 1px 0 rgba(255,255,255,.82)', position: 'relative', overflow: 'hidden', height: '50px' }}>
                  <div aria-hidden="true" style={{ position: 'absolute', inset: '0 auto auto 0', width: '100%', height: 4, background: 'linear-gradient(90deg,#60a5fa,#2563eb 55%,#0f172a)' }} />
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 5 }}>Medicamento solicitado</div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', lineHeight: 1.3, overflowWrap: 'anywhere', letterSpacing: '-.02em', overflow: 'visible' }}>{medicationDisplay.title}</div>
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 9px', borderRadius: 999, background: 'linear-gradient(180deg,#eff6ff,#dbeafe)', border: '1px solid rgba(37,99,235,.18)', color: '#1d4ed8', fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.7)' }}>
                      Ficha ampliada
                    </div>
                  </div>

                </div>

                <div style={{ border: '1px solid rgba(223,227,235,.95)', borderRadius: 16, background: 'linear-gradient(180deg,#ffffff,#fbfdff)', padding: 12, boxShadow: '0 10px 24px rgba(15,23,42,.05), inset 0 1px 0 rgba(255,255,255,.82)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' }}>Información ampliada</div>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>Vista compacta</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMedicationDrawerMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                    {infoColumns.map((columnRows, columnIndex) => (
                      <div key={`info-column-${columnIndex}`} style={{ display: 'grid', gap: 6 }}>
                        {columnRows.map((row) => (
                          <div key={`info-${row.label}`} style={{ padding: '8px 9px', borderRadius: 14, border: '1px solid rgba(226,232,240,.8)', background: 'linear-gradient(180deg,#fbfdff,#f8fbff)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.76)' }}>
                            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>{row.label}</div>
                            <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 800, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{row.value}</div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ border: '1px solid rgba(245,158,11,.24)', borderRadius: 16, background: 'linear-gradient(180deg,#fffdf7,#fffbeb)', padding: 12, boxShadow: '0 10px 24px rgba(245,158,11,.08), inset 0 1px 0 rgba(255,255,255,.78)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                    <span style={{ width: 18, height: 18, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,.14)', color: '#b45309', fontSize: 11, fontWeight: 900 }}>i</span>
                    <div style={{ fontSize: 10, color: '#b45309', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' }}>Lectura rápida</div>
                  </div>
                  <div style={{ display: 'grid', gap: 5, fontSize: 11, color: '#6b7280', lineHeight: 1.35 }}>
                    <div>Chequeá marca, droga y presentación antes de derivar.</div>
                    <div>La condición de venta y la refrigeración anticipan requisitos operativos.</div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        );
      })() : null}

      <div className="toast-container" style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1200 }}>
        {toasts.map((t) => (
          <div
            key={t.id}
            style={{
              marginTop: 8,
              minWidth: 280,
              maxWidth: 440,
              overflow: 'hidden'
            }}
          >
            <div
              role={t.pedidoId != null ? 'button' : 'status'}
              tabIndex={t.pedidoId != null ? 0 : -1}
              onMouseEnter={() => clearToastTimer(t.id)}
              onMouseLeave={() => scheduleToastRemoval(t.id)}
              onPointerDown={(e) => handleToastPointerDown(e, t.id)}
              onPointerMove={(e) => handleToastPointerMove(e, t.id)}
              onPointerUp={(e) => finishToastSwipe(e, t.id)}
              onPointerCancel={(e) => finishToastSwipe(e, t.id)}
              onClick={() => focusPedidoFromToast(t)}
              onKeyDown={(e) => {
                if (t.pedidoId == null) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  focusPedidoFromToast(t);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: '#fff',
                color: '#253342',
                boxShadow: '0 18px 50px rgba(8,18,40,0.18), 0 6px 18px rgba(8,18,40,0.08)',
                borderRadius: 12,
                padding: '10px 12px',
                minWidth: 280,
                maxWidth: 440,
                height: 'auto',
                transform: `translate3d(${toastSwipeOffsets[t.id] || 0}px,0,0)`,
                opacity: 1 - Math.min((toastSwipeOffsets[t.id] || 0) / 180, 0.45),
                cursor: t.pedidoId != null ? 'pointer' : 'default',
                border: t.pedidoId != null ? '1px solid rgba(37,99,235,.18)' : '1px solid transparent',
                transition: (toastSwipeOffsets[t.id] || 0) > 0
                  ? 'none'
                  : 'transform .18s ease, box-shadow .18s ease, border-color .18s ease, opacity .18s ease'
              }}
            >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#F4F7FF',
                color: '#2365AB',
                fontWeight: 800,
                fontSize: 14
              }}
            >
              <span>↗</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ fontSize: 12, fontWeight: 800, lineHeight: '1.1', color: '#2365AB', marginBottom: 4 }}>
                {t.title || 'Nuevo pedido'}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, lineHeight: '1.15', wordBreak: 'break-word', maxWidth: 340 }}>
                {t.paciente || t.msg}
              </div>
              {t.subtitle ? (
                <div style={{ fontSize: 12, color: '#6B7D93', marginTop: 4, wordBreak: 'break-word', maxWidth: 340 }}>
                  {t.subtitle}
                </div>
              ) : null}
              {t.pedidoId != null ? (
                <div style={{ fontSize: 11, color: '#2365AB', marginTop: 6, fontWeight: 700 }}>
                  Ver pedido
                </div>
              ) : null}
            </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissToast(t.id);
                }}
                aria-label="Cerrar alerta"
                title="Cerrar alerta"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  border: '1px solid rgba(148,163,184,.28)',
                  background: 'linear-gradient(180deg,#ffffff,#f8fbff)',
                  color: '#64748b',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  cursor: 'pointer',
                  fontSize: 15,
                  fontWeight: 800
                }}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      {canAltaRapida ? (
        <AltaRapidaPedidoModal
          open={modalAltaRapidaOpen}
          onClose={() => setModalAltaRapidaOpen(false)}
          historialPacientes={altaRapidaPacientes}
          destino="derivacion"
          eyebrow="Gestión derivación"
          title="Alta rápida de pedido"
          subtitle="Creá un pedido en cola de derivación para asignarlo a un médico."
          confirmQueueLabel="Entra a la cola de derivación"
          onPedidoCreated={handleAltaRapidaPedidoCreated}
          onError={(msg) => {
            Swal.fire({ icon: 'error', title: 'Alta rápida', text: msg || 'No se pudo crear el pedido' });
          }}
        />
      ) : null}
    </section>
  );
}
